import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth, useSipPhone } from "@/App";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Phone,
  Search,
  Filter,
  RefreshCw,
  Loader2,
  Flag,
  Trash2,
  PhoneIncoming,
  PhoneOutgoing,
  PhoneOff,
  X,
  Edit3,
  FileText,
  Shield,
  Plus,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Play,
  Pause,
  Download,
  ChevronLeft,
  ChevronRight,
  Link2,
} from "lucide-react";

function formatDuration(seconds) {
  const s = parseInt(seconds) || 0;
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

function formatDateTime(isoStr) {
  if (!isoStr) return "";
  try {
    const d = new Date(isoStr);
    return d.toLocaleString("en-US", {
      timeZone: "America/New_York",
      month: "numeric",
      day: "numeric",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).replace(",", "");
  } catch { return isoStr; }
}

function formatDateOnly(isoStr) {
  if (!isoStr) return "";
  try {
    const d = new Date(isoStr);
    return d.toLocaleDateString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric" });
  } catch { return isoStr; }
}

const STATUS_COLORS = {
  flagged: "bg-yellow-600",
  known: "bg-blue-600",
  suspicious: "bg-orange-600",
  blocked: "bg-red-600",
  vip: "bg-purple-600",
  other: "bg-slate-600",
};

const CONTACT_STATUSES = ["flagged", "known", "suspicious", "blocked", "vip", "other"];

export default function Hotline() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { handlePhoneClick } = useSipPhone() || {};

  // Tabs
  const [activeTab, setActiveTab] = useState("calls");

  // Calls state
  const [calls, setCalls] = useState([]);
  const [searchMatchedIncidents, setSearchMatchedIncidents] = useState([]);
  const [searchMatchedContacts, setSearchMatchedContacts] = useState([]);
  const [matchTypeFilters, setMatchTypeFilters] = useState([]); // ["victim", "callback", "witness"]
  const [callerTypeFilter, setCallerTypeFilter] = useState(""); // "cv", "3rd_party"
  const [loadingCalls, setLoadingCalls] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Call filters (server-side)
  const [searchPhone, setSearchPhone] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Pagination
  const [callsPage, setCallsPage] = useState(1);
  const [callsPerPage, setCallsPerPage] = useState(100);

  // Call filters (client-side) + sort
  const [showCallFilters, setShowCallFilters] = useState(false);
  const [callSortOrder, setCallSortOrder] = useState("newest");
  const [filterDirection, setFilterDirection] = useState("");
  const [filterCallStatus, setFilterCallStatus] = useState("");
  const [filterOnlyFlagged, setFilterOnlyFlagged] = useState(false);
  const [filterLinkedToIncident, setFilterLinkedToIncident] = useState(false);
  const [filterCallbackType, setFilterCallbackType] = useState("");
  const [filterHasVisual, setFilterHasVisual] = useState(false);
  const [filterIsVictim, setFilterIsVictim] = useState(false);
  const [filterIsWitness, setFilterIsWitness] = useState(false);
  // Incident-level filters
  const [filterIncidentType, setFilterIncidentType] = useState("");
  const [filterDispatcher, setFilterDispatcher] = useState("");
  const [filterHasAnyProfile, setFilterHasAnyProfile] = useState(false);
  const [filterSpecificProfile, setFilterSpecificProfile] = useState("");
  const [filterReadyForArrest, setFilterReadyForArrest] = useState(false);
  const [filterProfileArrested, setFilterProfileArrested] = useState(false);
  const [filterArrests, setFilterArrests] = useState(false);
  const [filterHateCrime, setFilterHateCrime] = useState(false);
  const [filterWeapon, setFilterWeapon] = useState(false);
  const [filterInjuries, setFilterInjuries] = useState(false);
  const [filterMinorElderly, setFilterMinorElderly] = useState(false);
  const [filterPdActivated, setFilterPdActivated] = useState(false);
  const [filterHotlineCall, setFilterHotlineCall] = useState(false);
  const [filterReportFiled, setFilterReportFiled] = useState(false);
  const [filterFootageRequested, setFilterFootageRequested] = useState(false);
  const [filterFootageObtained, setFilterFootageObtained] = useState(false);
  // Reference data for dropdowns
  const [incidentTypes, setIncidentTypes] = useState([]);
  const [dispatchers, setDispatchers] = useState([]);
  const [profiles, setProfiles] = useState([]);

  // Contacts state
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [contactSearch, setContactSearch] = useState("");

  // Contact filters (client-side) + sort
  const [showContactFilters, setShowContactFilters] = useState(false);
  const [contactFilterStatus, setContactFilterStatus] = useState("");
  const [contactSortOrder, setContactSortOrder] = useState("newest");

  // Dispatch calls state
  const [dispatchCalls, setDispatchCalls] = useState([]);
  const [chaverimCalls, setChaverimCalls] = useState([]);
  const [loadingChaverim, setLoadingChaverim] = useState(false);
  const [chaverimSearch, setChaverimSearch] = useState("");
  const [loadingDispatch, setLoadingDispatch] = useState(false);
  const [dispatchSearch, setDispatchSearch] = useState("");
  const [dispatchFilterType, setDispatchFilterType] = useState(""); // browser, call_my_phone
  const [dispatchFilterStatus, setDispatchFilterStatus] = useState("");
  const [dispatchFilterUser, setDispatchFilterUser] = useState("");
  const [playingRecording, setPlayingRecording] = useState(null); // call_sid of playing recording
  const [expandedRecording, setExpandedRecording] = useState(null); // call_sid of expanded player
  const [recordingBlobUrls, setRecordingBlobUrls] = useState({}); // { call_sid: blobUrl }
  const [recordingProgress, setRecordingProgress] = useState(0); // 0-1
  const [recordingDuration, setRecordingDuration] = useState(0);
  const audioRef = useRef(null); // single reusable audio element

  // All Numbers tab
  const [allNumbers, setAllNumbers] = useState([]);
  const [loadingAllNumbers, setLoadingAllNumbers] = useState(false);
  const [allNumSearch, setAllNumSearch] = useState("");
  const [allNumMatchFilter, setAllNumMatchFilter] = useState([]); // ["callback","victim","witness"]
  const [allNumCallerFilter, setAllNumCallerFilter] = useState(""); // "cv", "3rd_party"
  const [allNumSort, setAllNumSort] = useState("incidents"); // "incidents", "newest", "oldest", "phone"
  const [allNumFlaggedOnly, setAllNumFlaggedOnly] = useState(false);
  const [allNumArrestOnly, setAllNumArrestOnly] = useState(false);

  // Detail modal (for viewing a call/contact + linked incidents + call history)
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailPhone, setDetailPhone] = useState("");
  const [detailContact, setDetailContact] = useState(null);
  const [detailIncidents, setDetailIncidents] = useState([]);
  const [loadingIncidents, setLoadingIncidents] = useState(false);
  const [detailTab, setDetailTab] = useState("incidents"); // "incidents" | "history"
  const [detailCallHistory, setDetailCallHistory] = useState([]);
  const [loadingCallHistory, setLoadingCallHistory] = useState(false);

  // Link to incident modal
  const [linkIncidentOpen, setLinkIncidentOpen] = useState(false);
  const [linkSearch, setLinkSearch] = useState("");
  const [linkResults, setLinkResults] = useState([]);
  const [linkSearching, setLinkSearching] = useState(false);
  const [linkCallerType, setLinkCallerType] = useState("");
  const [linkHasVisual, setLinkHasVisual] = useState(false);
  const [linkLiveCallOpen, setLinkLiveCallOpen] = useState(false);
  const [liveCallList, setLiveCallList] = useState([]);
  const [liveCallSearch, setLiveCallSearch] = useState("");
  const [liveCallLoading, setLiveCallLoading] = useState(false);
  const [linkingToLiveCall, setLinkingToLiveCall] = useState(null);
  const [linkingTo, setLinkingTo] = useState(null); // incident_id being linked

  // Flag/Edit modal
  const [flagOpen, setFlagOpen] = useState(false);
  const [flagData, setFlagData] = useState({ phone_number: "", name: "", status: "flagged", notes: "" });
  const [flagIsEdit, setFlagIsEdit] = useState(false);
  const [flagPhoneEditable, setFlagPhoneEditable] = useState(false);

  const hasAccess = user?.role === "admin" || user?.hotline_access;

  // ---- Fetch calls ----
  const fetchCalls = useCallback(async (isRefresh = false) => {
    if (!hasAccess) return;
    try {
      if (isRefresh) setRefreshing(true); else setLoadingCalls(true);
      const params = new URLSearchParams();
      if (searchPhone) params.append("search", searchPhone);
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      const resp = await axios.get(`${API}/hotline/calls?${params}`, { withCredentials: true });
      setCalls(resp.data.calls || []);
      setSearchMatchedIncidents(resp.data.matched_incidents || []);
      setSearchMatchedContacts(resp.data.matched_contacts || []);
    } catch (err) {
      console.error("Error fetching hotline calls:", err);
      if (err.response?.status !== 403) toast.error("Failed to fetch calls");
    } finally {
      setLoadingCalls(false);
      setRefreshing(false);
    }
  }, [hasAccess, searchPhone, dateFrom, dateTo]);

  // ---- Fetch contacts ----
  const fetchContacts = useCallback(async () => {
    if (!hasAccess) return;
    try {
      setLoadingContacts(true);
      const params = new URLSearchParams();
      if (contactSearch) params.append("search", contactSearch);
      const resp = await axios.get(`${API}/hotline/contacts?${params}`, { withCredentials: true });
      setContacts(resp.data.contacts || []);
    } catch (err) {
      console.error("Error fetching contacts:", err);
    } finally {
      setLoadingContacts(false);
    }
  }, [hasAccess, contactSearch]);

  // ---- Fetch dispatch calls ----
  const fetchDispatchCalls = useCallback(async () => {
    if (!hasAccess) return;
    try {
      setLoadingDispatch(true);
      const resp = await axios.get(`${API}/voice/dispatch-calls`, { withCredentials: true });
      setDispatchCalls(resp.data.calls || []);
    } catch (err) {
      console.error("Error fetching dispatch calls:", err);
    } finally {
      setLoadingDispatch(false);
    }
  }, [hasAccess]);

  // ---- Admin: force a stuck dispatch call to completed ----
  const forceHangup = useCallback(async (callSid) => {
    try {
      await axios.post(`${API}/voice/dispatch-calls/${callSid}/force-complete`, {}, { withCredentials: true });
      toast.success("Call force-completed");
      fetchDispatchCalls();
    } catch (err) {
      console.error("Error force-completing dispatch call:", err);
      toast.error(err.response?.data?.detail || "Failed to force hangup");
    }
  }, [fetchDispatchCalls]);

  // ---- Fetch Chaverim calls (6444 alert line) ----
  const fetchChaverimCalls = useCallback(async () => {
    if (!hasAccess) return;
    try {
      setLoadingChaverim(true);
      const resp = await axios.get(`${API}/incoming-calls/chaverim`, { withCredentials: true });
      setChaverimCalls(resp.data.calls || []);
    } catch (err) {
      console.error("Error fetching chaverim calls:", err);
    } finally {
      setLoadingChaverim(false);
    }
  }, [hasAccess]);

  const fetchAllNumbers = useCallback(async () => {
    if (!hasAccess) return;
    try {
      setLoadingAllNumbers(true);
      const resp = await axios.get(`${API}/hotline/all-numbers`, { withCredentials: true });
      setAllNumbers(resp.data.numbers || []);
    } catch (err) {
      console.error("Error fetching all numbers:", err);
    } finally {
      setLoadingAllNumbers(false);
    }
  }, [hasAccess]);

  // ---- Client-side filtered + sorted all numbers ----
  const filteredAllNumbers = useMemo(() => {
    let result = [...allNumbers];
    if (allNumSearch) {
      const q = allNumSearch.toLowerCase();
      result = result.filter(n =>
        n.phone.includes(q) || n.phone_normalized.includes(q.replace(/\D/g, "")) ||
        n.names?.some(name => name.toLowerCase().includes(q)) ||
        n.contact_name?.toLowerCase().includes(q)
      );
    }
    if (allNumMatchFilter.length > 0) {
      result = result.filter(n => allNumMatchFilter.every(t => n.match_types?.includes(t)));
    }
    if (allNumCallerFilter) {
      result = result.filter(n =>
        n.match_types?.includes("callback") &&
        (allNumCallerFilter === "cv" ? n.caller_types?.includes("cv") : n.caller_types?.some(t => t && t !== "cv"))
      );
    }
    if (allNumFlaggedOnly) {
      result = result.filter(n => n.is_flagged);
    }
    if (allNumArrestOnly) {
      result = result.filter(n => n.has_arrest);
    }
    // Sort
    if (allNumSort === "incidents") {
      result.sort((a, b) => b.incident_count - a.incident_count);
    } else if (allNumSort === "newest") {
      result.sort((a, b) => (b.latest_date || "").localeCompare(a.latest_date || ""));
    } else if (allNumSort === "oldest") {
      result.sort((a, b) => (a.latest_date || "").localeCompare(b.latest_date || ""));
    } else if (allNumSort === "phone") {
      result.sort((a, b) => a.phone_normalized.localeCompare(b.phone_normalized));
    }
    return result;
  }, [allNumbers, allNumSearch, allNumMatchFilter, allNumCallerFilter, allNumFlaggedOnly, allNumArrestOnly, allNumSort]);

  const toggleAllNumMatchFilter = (type) => {
    setAllNumMatchFilter(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);
  };

  // ---- Client-side filtered + sorted calls ----
  const filteredCalls = useMemo(() => {
    let result = [...calls];
    if (filterDirection) {
      result = result.filter(c => {
        if (filterDirection === "inbound") return c.direction === "inbound";
        if (filterDirection === "outbound") return c.direction?.startsWith("outbound");
        return c.direction === filterDirection;
      });
    }
    if (filterCallStatus) {
      result = result.filter(c => c.status === filterCallStatus);
    }
    if (filterOnlyFlagged) {
      result = result.filter(c => c.is_flagged);
    }
    if (filterLinkedToIncident) {
      result = result.filter(c => c.has_incidents);
    }
    if (filterCallbackType) {
      result = result.filter(c => c.callback_caller_types?.includes(filterCallbackType));
    }
    if (filterHasVisual) {
      result = result.filter(c => c.callback_has_visual);
    }
    if (filterIsVictim) {
      result = result.filter(c => c.is_victim);
    }
    if (filterIsWitness) {
      result = result.filter(c => c.is_witness);
    }
    // Incident-level filters
    if (filterIncidentType) {
      result = result.filter(c => c.incident_types?.includes(filterIncidentType));
    }
    if (filterDispatcher) {
      result = result.filter(c => c.incident_dispatchers?.includes(filterDispatcher));
    }
    if (filterHasAnyProfile) {
      result = result.filter(c => c.incident_has_profile);
    }
    if (filterSpecificProfile) {
      result = result.filter(c => c.incident_linked_profile_ids?.includes(filterSpecificProfile));
    }
    if (filterReadyForArrest) {
      result = result.filter(c => c.incident_ready_for_arrest);
    }
    if (filterProfileArrested) {
      if (filterSpecificProfile) {
        const prof = profiles.find(p => p.profile_id === filterSpecificProfile);
        const hasArrests = prof?.linked_arrests?.length > 0 || prof?.arrests?.length > 0;
        if (!hasArrests) result = [];
      } else {
        result = result.filter(c => {
          const pids = c.incident_linked_profile_ids || [];
          return pids.some(pid => {
            const prof = profiles.find(p => p.profile_id === pid);
            return prof?.linked_arrests?.length > 0 || prof?.arrests?.length > 0;
          });
        });
      }
    }
    if (filterArrests) {
      result = result.filter(c => c.incident_has_arrest);
    }
    if (filterHateCrime) {
      result = result.filter(c => c.incident_has_hate_crime);
    }
    if (filterWeapon) {
      result = result.filter(c => c.incident_has_weapon);
    }
    if (filterInjuries) {
      result = result.filter(c => c.incident_has_injuries);
    }
    if (filterMinorElderly) {
      result = result.filter(c => c.incident_has_minor_elderly);
    }
    if (filterPdActivated) {
      result = result.filter(c => c.incident_pd_activated);
    }
    if (filterHotlineCall) {
      result = result.filter(c => c.incident_is_hotline_call);
    }
    if (filterReportFiled) {
      result = result.filter(c => c.incident_report_filed);
    }
    if (filterFootageRequested) {
      result = result.filter(c => c.incident_footage_requested);
    }
    if (filterFootageObtained) {
      result = result.filter(c => c.incident_footage_obtained);
    }
    result.sort((a, b) => {
      const da = new Date(a.received_at || 0);
      const db = new Date(b.received_at || 0);
      return callSortOrder === "newest" ? db - da : da - db;
    });
    return result;
  }, [calls, filterDirection, filterCallStatus, filterOnlyFlagged, filterLinkedToIncident, filterCallbackType, filterHasVisual, filterIsVictim, filterIsWitness, filterIncidentType, filterDispatcher, filterHasAnyProfile, filterSpecificProfile, filterReadyForArrest, filterProfileArrested, profiles, filterArrests, filterHateCrime, filterWeapon, filterInjuries, filterMinorElderly, filterPdActivated, filterHotlineCall, filterReportFiled, filterFootageRequested, filterFootageObtained, callSortOrder]);

  // Filter matched incidents by match type and caller type
  const filteredMatchedIncidents = useMemo(() => {
    let result = searchMatchedIncidents;
    if (matchTypeFilters.length > 0) {
      result = result.filter(inc =>
        matchTypeFilters.some(t => inc.match_types?.includes(t))
      );
    }
    if (callerTypeFilter) {
      const isCV = callerTypeFilter === "cv";
      result = result.filter(inc =>
        inc.match_types?.includes("callback") &&
        (isCV ? inc.callback_caller_type === "cv" : inc.callback_caller_type && inc.callback_caller_type !== "cv")
      );
    }
    return result;
  }, [searchMatchedIncidents, matchTypeFilters, callerTypeFilter]);

  const toggleMatchTypeFilter = (type) => {
    setMatchTypeFilters(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  // Pagination: show all when searching, paginate otherwise
  const isSearching = !!searchPhone.trim();
  const totalCallPages = isSearching ? 1 : Math.max(1, Math.ceil(filteredCalls.length / callsPerPage));
  const paginatedCalls = useMemo(() => {
    if (isSearching) return filteredCalls; // Show ALL when searching
    const start = (callsPage - 1) * callsPerPage;
    return filteredCalls.slice(start, start + callsPerPage);
  }, [filteredCalls, callsPage, callsPerPage, isSearching]);

  // Reset page when filters change
  useEffect(() => { setCallsPage(1); }, [filteredCalls.length, callsPerPage]);

  // ---- Client-side filtered + sorted contacts ----
  const filteredContacts = useMemo(() => {
    let result = [...contacts];
    if (contactFilterStatus) {
      result = result.filter(c => c.status === contactFilterStatus);
    }
    result.sort((a, b) => {
      if (contactSortOrder === "name") {
        return (a.name || "").localeCompare(b.name || "");
      }
      const da = new Date(a.created_at || 0);
      const db = new Date(b.created_at || 0);
      return contactSortOrder === "oldest" ? da - db : db - da;
    });
    return result;
  }, [contacts, contactFilterStatus, contactSortOrder]);

  // ---- Active filter count helpers ----
  const activeCallFilterCount = [filterDirection, filterCallStatus, filterOnlyFlagged, filterLinkedToIncident, filterCallbackType, filterHasVisual, filterIsVictim, filterIsWitness, filterIncidentType, filterDispatcher, filterHasAnyProfile, filterSpecificProfile, filterReadyForArrest, filterProfileArrested, filterArrests, filterHateCrime, filterWeapon, filterInjuries, filterMinorElderly, filterPdActivated, filterHotlineCall, filterReportFiled, filterFootageRequested, filterFootageObtained, dateFrom, dateTo].filter(Boolean).length;
  const activeContactFilterCount = [contactFilterStatus].filter(Boolean).length;

  // ---- Fetch incidents for a phone number ----
  const fetchIncidents = async (phone) => {
    setLoadingIncidents(true);
    try {
      const resp = await axios.get(`${API}/hotline/incidents/${encodeURIComponent(phone)}`, { withCredentials: true });
      setDetailIncidents(resp.data.incidents || []);
    } catch (err) {
      console.error("Error fetching incidents:", err);
      setDetailIncidents([]);
    } finally {
      setLoadingIncidents(false);
    }
  };

  // ---- Fetch call history for a phone number ----
  const fetchCallHistory = async (phone) => {
    setLoadingCallHistory(true);
    try {
      const resp = await axios.get(`${API}/hotline/call-history/${encodeURIComponent(phone)}`, { withCredentials: true });
      setDetailCallHistory(resp.data.calls || []);
    } catch (err) {
      console.error("Error fetching call history:", err);
      setDetailCallHistory([]);
    } finally {
      setLoadingCallHistory(false);
    }
  };

  // ---- Open detail modal ----
  const openDetail = (phone, contactInfo = null) => {
    setDetailPhone(phone);
    setDetailContact(contactInfo);
    setDetailTab("incidents");
    setDetailOpen(true);
    fetchIncidents(phone);
    fetchCallHistory(phone);
  };

  // ---- Link callback to existing incident ----
  const searchIncidentsForLink = async (query) => {
    if (!query || query.length < 2) { setLinkResults([]); return; }
    setLinkSearching(true);
    try {
      const res = await axios.get(`${API}/incidents`, {
        params: { search: query, limit: 20 },
        withCredentials: true,
      });
      setLinkResults(Array.isArray(res.data) ? res.data : []);
    } catch { setLinkResults([]); }
    finally { setLinkSearching(false); }
  };

  const handleLinkCallback = async (incidentId) => {
    if (!detailPhone || !incidentId) return;
    setLinkingTo(incidentId);
    try {
      await axios.post(`${API}/incidents/${incidentId}/add-callback`, {
        number: detailPhone,
        caller_type: linkCallerType,
        has_visual: linkHasVisual,
      }, { withCredentials: true });
      toast.success("Callback linked to incident");
      setLinkIncidentOpen(false);
      setLinkSearch("");
      setLinkResults([]);
      setLinkCallerType("");
      setLinkHasVisual(false);
      // Refresh incidents for this phone
      fetchIncidents(detailPhone);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to link callback");
    } finally {
      setLinkingTo(null);
    }
  };

  // ---- Add callback to active live call ----
  const fetchActiveLiveCalls = async () => {
    setLiveCallLoading(true);
    try {
      const res = await axios.get(`${API}/live-calls`, { withCredentials: true });
      setLiveCallList(Array.isArray(res.data) ? res.data : []);
    } catch {
      setLiveCallList([]);
    } finally {
      setLiveCallLoading(false);
    }
  };

  const handleLinkLiveCallCallback = async (callId) => {
    if (!detailPhone || !callId) return;
    setLinkingToLiveCall(callId);
    try {
      await axios.post(`${API}/live-calls/${callId}/add-callback`, {
        number: detailPhone,
        caller_type: linkCallerType,
        has_visual: linkHasVisual,
      }, { withCredentials: true });
      toast.success("Callback added to live call");
      setLinkLiveCallOpen(false);
      setLiveCallSearch("");
      setLinkCallerType("");
      setLinkHasVisual(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add to live call");
    } finally {
      setLinkingToLiveCall(null);
    }
  };

  // ---- Open flag modal (from call or contact) ----
  const openFlag = (phone, existingContact = null) => {
    setFlagIsEdit(!!existingContact?.contact_id);
    setFlagPhoneEditable(false);
    setFlagData({
      phone_number: phone,
      name: existingContact?.contact_name || existingContact?.name || "",
      status: existingContact?.contact_status || existingContact?.status || "flagged",
      notes: existingContact?.contact_notes || existingContact?.notes || "",
    });
    setFlagOpen(true);
  };

  // ---- Open flag modal for manual new contact ----
  const openNewContact = () => {
    setFlagIsEdit(false);
    setFlagPhoneEditable(true);
    setFlagData({ phone_number: "", name: "", status: "flagged", notes: "" });
    setFlagOpen(true);
  };

  // ---- Save flag ----
  const saveFlag = async () => {
    if (!flagData.phone_number.trim()) {
      toast.error("Phone number is required");
      return;
    }
    try {
      await axios.post(`${API}/hotline/contacts`, flagData, { withCredentials: true });
      toast.success("Contact saved");
      setFlagOpen(false);
      fetchCalls(true);
      fetchContacts();
      if (detailOpen && detailPhone === flagData.phone_number) {
        fetchIncidents(detailPhone);
      }
    } catch (err) {
      toast.error("Failed to save contact");
    }
  };

  // ---- Delete flag ----
  const deleteFlag = async (contactId) => {
    if (!confirm("Remove flag from this number?")) return;
    try {
      await axios.delete(`${API}/hotline/contacts/${contactId}`, { withCredentials: true });
      toast.success("Flag removed");
      fetchCalls(true);
      fetchContacts();
      if (detailOpen) {
        setDetailContact(null);
      }
    } catch (err) {
      toast.error("Failed to remove flag");
    }
  };

  // Clear all call filters
  const clearCallFilters = () => {
    setSearchPhone("");
    setSearchMatchedIncidents([]);
    setSearchMatchedContacts([]);
    setMatchTypeFilters([]);
    setCallerTypeFilter("");
    setDateFrom("");
    setDateTo("");
    setFilterDirection("");
    setFilterCallStatus("");
    setFilterOnlyFlagged(false);
    setFilterLinkedToIncident(false);
    setFilterCallbackType("");
    setFilterHasVisual(false);
    setFilterIsVictim(false);
    setFilterIsWitness(false);
    setFilterIncidentType("");
    setFilterDispatcher("");
    setFilterHasAnyProfile(false);
    setFilterSpecificProfile("");
    setFilterReadyForArrest(false);
    setFilterProfileArrested(false);
    setFilterArrests(false);
    setFilterHateCrime(false);
    setFilterWeapon(false);
    setFilterInjuries(false);
    setFilterMinorElderly(false);
    setFilterPdActivated(false);
    setFilterHotlineCall(false);
    setFilterReportFiled(false);
    setFilterFootageRequested(false);
    setFilterFootageObtained(false);
    setCallSortOrder("newest");
    setTimeout(() => fetchCalls(), 50);
  };

  // Clear all contact filters
  const clearContactFilters = () => {
    setContactSearch("");
    setContactFilterStatus("");
    setContactSortOrder("newest");
    setTimeout(() => fetchContacts(), 50);
  };

  // Fetch reference data (incident types, dispatchers, profiles)
  useEffect(() => {
    if (hasAccess) {
      axios.get(`${API}/incident-types`, { withCredentials: true })
        .then(res => setIncidentTypes(res.data || []))
        .catch(() => {});
      axios.get(`${API}/units/dispatchers`, { withCredentials: true })
        .then(res => setDispatchers(res.data || []))
        .catch(() => {});
      axios.get(`${API}/perpetrator-profiles`, { withCredentials: true })
        .then(res => setProfiles(res.data?.profiles || res.data || []))
        .catch(() => {});
    }
  }, [hasAccess]);

  // Initial fetch
  useEffect(() => {
    if (hasAccess) {
      fetchCalls();
      fetchContacts();
    }
  }, []);

  const renderPagination = () => (
    !loadingCalls && filteredCalls.length > 0 ? (
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-slate-500">
            {isSearching ? (
              <>{filteredCalls.length} result{filteredCalls.length !== 1 ? "s" : ""} (showing all)</>
            ) : (
              <>Showing {Math.min((callsPage - 1) * callsPerPage + 1, filteredCalls.length)}-{Math.min(callsPage * callsPerPage, filteredCalls.length)} of {filteredCalls.length}</>
            )}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-slate-500 mr-1">Per page:</span>
          {[5, 10, 20, 50, 100, 200].map((n) => (
            <button
              key={n}
              onClick={() => setCallsPerPage(n)}
              className={`px-1.5 py-0.5 text-xs rounded ${callsPerPage === n ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-400 hover:bg-slate-700"}`}
            >
              {n}
            </button>
          ))}
        </div>
        {!isSearching && totalCallPages > 1 && (
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={callsPage <= 1} onClick={() => setCallsPage(callsPage - 1)}>
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            {Array.from({ length: totalCallPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalCallPages || Math.abs(p - callsPage) <= 2)
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                acc.push(p);
                return acc;
              }, [])
              .map((p, idx) =>
                p === "..." ? (
                  <span key={`dots-${idx}`} className="text-xs text-slate-500 px-1">...</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setCallsPage(p)}
                    className={`h-7 min-w-[28px] px-1.5 text-xs rounded ${callsPage === p ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-400 hover:bg-slate-700"}`}
                  >
                    {p}
                  </button>
                )
              )}
            <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={callsPage >= totalCallPages} onClick={() => setCallsPage(callsPage + 1)}>
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>
    ) : null
  );

  const applyCallFilters = () => fetchCalls();
  const applyContactSearch = () => fetchContacts();

  // ---- Access denied ----
  if (!hasAccess) {
    return (
      <div className="flex items-center justify-center h-full min-h-[60vh]">
        <Card className="max-w-md bg-slate-900 border-slate-700">
          <CardContent className="pt-6 text-center">
            <Shield className="h-12 w-12 mx-auto mb-4 text-slate-500" />
            <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
            <p className="text-slate-400 text-sm">
              You do not have permission to view hotline data.
              Contact an administrator to request hotline access.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 space-y-3 max-w-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Phone className="h-5 w-5 sm:h-6 sm:w-6" />
          <h1 className="text-lg sm:text-xl font-bold">Hotline</h1>
        </div>
        <Button onClick={() => activeTab === "calls" ? fetchCalls(true) : activeTab === "dispatch" ? fetchDispatchCalls() : activeTab === "numbers" ? fetchAllNumbers() : activeTab === "chaverim" ? fetchChaverimCalls() : fetchContacts()} disabled={refreshing} variant="outline" size="sm">
          <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-800 rounded-lg p-1 overflow-x-auto no-scrollbar">
        <button
          onClick={() => setActiveTab("calls")}
          className={`flex-shrink-0 px-2 sm:px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${activeTab === "calls" ? "bg-slate-700 text-white" : "text-slate-400 hover:text-white"}`}
        >
          <PhoneIncoming className="h-3.5 w-3.5 sm:h-4 sm:w-4 inline mr-0.5 sm:mr-1 -mt-0.5" />
          <span className="hidden sm:inline">Calls</span><span className="sm:hidden">Calls</span>
          {calls.length > 0 && <span className="text-[10px] sm:text-xs text-slate-400 ml-0.5 sm:ml-1">({calls.length})</span>}
        </button>
        {(user?.hotline_dispatch_access || user?.role === "admin") && (
          <button
            onClick={() => { setActiveTab("dispatch"); fetchDispatchCalls(); }}
            className={`flex-shrink-0 px-2 sm:px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${activeTab === "dispatch" ? "bg-slate-700 text-white" : "text-slate-400 hover:text-white"}`}
          >
            <PhoneOutgoing className="h-3.5 w-3.5 sm:h-4 sm:w-4 inline mr-0.5 sm:mr-1 -mt-0.5" />
            <span className="hidden sm:inline">Dispatch</span><span className="sm:hidden">Disp</span>
            {dispatchCalls.length > 0 && <span className="text-[10px] sm:text-xs text-slate-400 ml-0.5 sm:ml-1">({dispatchCalls.length})</span>}
          </button>
        )}
        <button
          onClick={() => { setActiveTab("contacts"); fetchContacts(); }}
          className={`flex-shrink-0 px-2 sm:px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${activeTab === "contacts" ? "bg-slate-700 text-white" : "text-slate-400 hover:text-white"}`}
        >
          <Flag className="h-3.5 w-3.5 sm:h-4 sm:w-4 inline mr-0.5 sm:mr-1 -mt-0.5" />
          <span className="hidden sm:inline">Contacts</span><span className="sm:hidden">Cont</span>
          {contacts.length > 0 && <span className="text-[10px] sm:text-xs text-slate-400 ml-0.5 sm:ml-1">({contacts.length})</span>}
        </button>
        <button
          onClick={() => { setActiveTab("numbers"); fetchAllNumbers(); }}
          className={`flex-shrink-0 px-2 sm:px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${activeTab === "numbers" ? "bg-slate-700 text-white" : "text-slate-400 hover:text-white"}`}
        >
          <Phone className="h-3.5 w-3.5 sm:h-4 sm:w-4 inline mr-0.5 sm:mr-1 -mt-0.5" />
          <span className="hidden sm:inline">All Numbers</span><span className="sm:hidden">All #</span>
          {allNumbers.length > 0 && <span className="text-[10px] sm:text-xs text-slate-400 ml-0.5 sm:ml-1">({allNumbers.length})</span>}
        </button>
        <button
          onClick={() => { setActiveTab("chaverim"); fetchChaverimCalls(); }}
          className={`flex-shrink-0 px-2 sm:px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${activeTab === "chaverim" ? "bg-amber-600 text-black" : "text-amber-400 hover:text-amber-300"}`}
        >
          <PhoneIncoming className="h-3.5 w-3.5 sm:h-4 sm:w-4 inline mr-0.5 sm:mr-1 -mt-0.5" />
          Chaverim
          {chaverimCalls.length > 0 && <span className="text-[10px] sm:text-xs opacity-80 ml-0.5 sm:ml-1">({chaverimCalls.length})</span>}
        </button>
      </div>

      {/* =========== CALLS TAB =========== */}
      {activeTab === "calls" && (
        <>
          {/* Search bar + Filters toggle */}
          <Card className="bg-slate-900 border-slate-700">
            <CardContent className="pt-3 pb-3">
              <div className="flex gap-1.5 sm:gap-2 items-center">
                <div className="flex-1 min-w-0">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-500" />
                    <Input
                      placeholder="Phone, name..."
                      value={searchPhone}
                      onChange={(e) => setSearchPhone(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && applyCallFilters()}
                      className="pl-8 h-9 text-sm bg-slate-800 border-slate-700"
                    />
                  </div>
                </div>
                {searchPhone.trim() && searchPhone.replace(/\D/g, '').length >= 10 && (
                  <Button variant="outline" size="sm" className="h-9 px-2" title="Lookup phone history" onClick={() => openDetail(searchPhone.replace(/\D/g, ''))}>
                    <Phone className="h-3.5 w-3.5" />
                  </Button>
                )}
                <Button variant="outline" size="sm" className="h-9 px-2 sm:px-3" onClick={() => setShowCallFilters(!showCallFilters)}>
                  <Filter className="h-3.5 w-3.5 sm:mr-1" />
                  <span className="hidden sm:inline">Filters</span>
                  {activeCallFilterCount > 0 && (
                    <Badge className="ml-1 sm:ml-1.5 h-4 w-4 p-0 flex items-center justify-center text-[9px] bg-blue-600">{activeCallFilterCount}</Badge>
                  )}
                </Button>
                <Button onClick={applyCallFilters} size="sm" className="h-9 px-2 sm:px-3">
                  <Search className="h-3.5 w-3.5 sm:mr-1" />
                  <span className="hidden sm:inline">Search</span>
                </Button>
              </div>

              {/* Expandable filter panel */}
              {showCallFilters && (
                <div className="mt-3 pt-3 border-t border-slate-800 space-y-3">
                  {/* Primary filters row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="bg-slate-800/50 rounded-lg p-3">
                      <Label className="text-xs text-slate-400 uppercase tracking-wide mb-2 block">Date From</Label>
                      <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 text-sm bg-slate-900 border-slate-700" />
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-3">
                      <Label className="text-xs text-slate-400 uppercase tracking-wide mb-2 block">Date To</Label>
                      <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 text-sm bg-slate-900 border-slate-700" />
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-3">
                      <Label className="text-xs text-slate-400 uppercase tracking-wide mb-2 block">Direction</Label>
                      <Select value={filterDirection || "all"} onValueChange={(v) => setFilterDirection(v === "all" ? "" : v)}>
                        <SelectTrigger className="h-9 bg-slate-900 border-slate-700">
                          <SelectValue placeholder="All" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Directions</SelectItem>
                          <SelectItem value="inbound">Inbound</SelectItem>
                          <SelectItem value="outbound">Outbound</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-3">
                      <Label className="text-xs text-slate-400 uppercase tracking-wide mb-2 block">Call Status</Label>
                      <Select value={filterCallStatus || "all"} onValueChange={(v) => setFilterCallStatus(v === "all" ? "" : v)}>
                        <SelectTrigger className="h-9 bg-slate-900 border-slate-700">
                          <SelectValue placeholder="All" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Statuses</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="no-answer">No Answer</SelectItem>
                          <SelectItem value="busy">Busy</SelectItem>
                          <SelectItem value="failed">Failed</SelectItem>
                          <SelectItem value="canceled">Canceled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Sort + Callback type */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="bg-slate-800/50 rounded-lg p-3">
                      <Label className="text-xs text-slate-400 uppercase tracking-wide mb-2 block">Sort Order</Label>
                      <Select value={callSortOrder} onValueChange={setCallSortOrder}>
                        <SelectTrigger className="h-9 bg-slate-900 border-slate-700">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="newest">Newest First</SelectItem>
                          <SelectItem value="oldest">Oldest First</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-3">
                      <Label className="text-xs text-slate-400 uppercase tracking-wide mb-2 block">Callback Type</Label>
                      <Select value={filterCallbackType || "all"} onValueChange={(v) => setFilterCallbackType(v === "all" ? "" : v)}>
                        <SelectTrigger className="h-9 bg-slate-900 border-slate-700">
                          <SelectValue placeholder="All" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Types</SelectItem>
                          <SelectItem value="cv">CV</SelectItem>
                          <SelectItem value="3rd_party">3rd Party</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Incident type + Dispatcher dropdowns */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="bg-slate-800/50 rounded-lg p-3">
                      <Label className="text-xs text-slate-400 uppercase tracking-wide mb-2 block">Incident Type</Label>
                      <Select value={filterIncidentType || "all"} onValueChange={(v) => setFilterIncidentType(v === "all" ? "" : v)}>
                        <SelectTrigger className="h-9 bg-slate-900 border-slate-700">
                          <SelectValue placeholder="All Types" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Types</SelectItem>
                          {incidentTypes.map((t) => (
                            <SelectItem key={t.type_id || t.name} value={t.name}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-3">
                      <Label className="text-xs text-slate-400 uppercase tracking-wide mb-2 block">Dispatcher</Label>
                      <Select value={filterDispatcher || "all"} onValueChange={(v) => setFilterDispatcher(v === "all" ? "" : v)}>
                        <SelectTrigger className="h-9 bg-slate-900 border-slate-700">
                          <SelectValue placeholder="All Dispatchers" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Dispatchers</SelectItem>
                          {dispatchers.map((d) => (
                            <SelectItem key={d.unit_id || d.name} value={d.name}>{d.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Quick toggle filters */}
                  <div className="grid grid-cols-1 gap-3">
                    <div className="bg-yellow-950/20 border border-yellow-900/30 rounded-lg p-3">
                      <Label className="text-xs text-yellow-400 uppercase tracking-wide mb-3 block">Contact Flags</Label>
                      <div className="flex flex-wrap gap-2">
                        <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${filterOnlyFlagged ? 'bg-yellow-600/30 border border-yellow-500' : 'bg-slate-800/50 hover:bg-slate-700/50 border border-transparent'}`}>
                          <Checkbox checked={filterOnlyFlagged} onCheckedChange={(c) => setFilterOnlyFlagged(!!c)} className="hidden" />
                          <Flag className="h-3 w-3" />
                          <span className={`text-sm ${filterOnlyFlagged ? 'text-yellow-400' : 'text-slate-300'}`}>Flagged Only</span>
                        </label>
                      </div>
                    </div>
                    <div className="bg-blue-950/20 border border-blue-900/30 rounded-lg p-3">
                      <Label className="text-xs text-blue-400 uppercase tracking-wide mb-3 block">Incident Filters</Label>
                      <div className="flex flex-wrap gap-2">
                        <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${filterLinkedToIncident ? 'bg-blue-600/30 border border-blue-500' : 'bg-slate-800/50 hover:bg-slate-700/50 border border-transparent'}`}>
                          <Checkbox checked={filterLinkedToIncident} onCheckedChange={(c) => setFilterLinkedToIncident(!!c)} className="hidden" />
                          <FileText className="h-3 w-3" />
                          <span className={`text-sm ${filterLinkedToIncident ? 'text-blue-400' : 'text-slate-300'}`}>Linked to Incident</span>
                        </label>
                        <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${filterHasVisual ? 'bg-green-600/30 border border-green-500' : 'bg-slate-800/50 hover:bg-slate-700/50 border border-transparent'}`}>
                          <Checkbox checked={filterHasVisual} onCheckedChange={(c) => setFilterHasVisual(!!c)} className="hidden" />
                          <span className={`text-sm ${filterHasVisual ? 'text-green-400' : 'text-slate-300'}`}>Has Visual</span>
                        </label>
                        <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${filterHotlineCall ? 'bg-blue-600/30 border border-blue-500' : 'bg-slate-800/50 hover:bg-slate-700/50 border border-transparent'}`}>
                          <Checkbox checked={filterHotlineCall} onCheckedChange={(c) => setFilterHotlineCall(!!c)} className="hidden" />
                          <span className={`text-sm ${filterHotlineCall ? 'text-blue-400' : 'text-slate-300'}`}>Hotline Call</span>
                        </label>
                        <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${filterIsVictim ? 'bg-orange-600/30 border border-orange-500' : 'bg-slate-800/50 hover:bg-slate-700/50 border border-transparent'}`}>
                          <Checkbox checked={filterIsVictim} onCheckedChange={(c) => setFilterIsVictim(!!c)} className="hidden" />
                          <span className={`text-sm ${filterIsVictim ? 'text-orange-400' : 'text-slate-300'}`}>Is Victim</span>
                        </label>
                        <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${filterIsWitness ? 'bg-teal-600/30 border border-teal-500' : 'bg-slate-800/50 hover:bg-slate-700/50 border border-transparent'}`}>
                          <Checkbox checked={filterIsWitness} onCheckedChange={(c) => setFilterIsWitness(!!c)} className="hidden" />
                          <span className={`text-sm ${filterIsWitness ? 'text-teal-400' : 'text-slate-300'}`}>Is Witness</span>
                        </label>
                      </div>
                    </div>
                    <div className="bg-cyan-950/20 border border-cyan-900/30 rounded-lg p-3">
                      <Label className="text-xs text-cyan-400 uppercase tracking-wide mb-3 block">Profile Filters</Label>
                      <div className="flex flex-wrap items-center gap-2">
                        <Select value={filterSpecificProfile || "all"} onValueChange={(v) => setFilterSpecificProfile(v === "all" ? "" : v)}>
                          <SelectTrigger className="h-8 w-48 bg-slate-900 border-slate-700 text-sm">
                            <SelectValue placeholder="Select Profile" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Profiles</SelectItem>
                            {profiles.map((p) => (
                              <SelectItem key={p.profile_id} value={p.profile_id}>{p.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${filterHasAnyProfile ? 'bg-cyan-600/30 border border-cyan-500' : 'bg-slate-800/50 hover:bg-slate-700/50 border border-transparent'}`}>
                          <Checkbox checked={filterHasAnyProfile} onCheckedChange={(c) => setFilterHasAnyProfile(!!c)} className="hidden" />
                          <span className={`text-sm ${filterHasAnyProfile ? 'text-cyan-400' : 'text-slate-300'}`}>Has Any Profile</span>
                        </label>
                        <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${filterReadyForArrest ? 'bg-amber-600/30 border border-amber-500' : 'bg-slate-800/50 hover:bg-slate-700/50 border border-transparent'}`}>
                          <Checkbox checked={filterReadyForArrest} onCheckedChange={(c) => setFilterReadyForArrest(!!c)} className="hidden" />
                          <span className={`text-sm ${filterReadyForArrest ? 'text-amber-400' : 'text-slate-300'}`}>Ready for Arrest</span>
                        </label>
                        <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${filterProfileArrested ? 'bg-emerald-600/30 border border-emerald-500' : 'bg-slate-800/50 hover:bg-slate-700/50 border border-transparent'}`}>
                          <Checkbox checked={filterProfileArrested} onCheckedChange={(c) => setFilterProfileArrested(!!c)} className="hidden" />
                          <span className={`text-sm ${filterProfileArrested ? 'text-emerald-400' : 'text-slate-300'}`}>Arrested</span>
                        </label>
                      </div>
                    </div>
                    <div className="bg-red-950/20 border border-red-900/30 rounded-lg p-3">
                      <Label className="text-xs text-red-400 uppercase tracking-wide mb-3 block">Critical Flags</Label>
                      <div className="flex flex-wrap gap-2">
                        <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${filterArrests ? 'bg-red-600/30 border border-red-500' : 'bg-slate-800/50 hover:bg-slate-700/50 border border-transparent'}`}>
                          <Checkbox checked={filterArrests} onCheckedChange={(c) => setFilterArrests(!!c)} className="hidden" />
                          <span className={`text-sm ${filterArrests ? 'text-red-400' : 'text-slate-300'}`}>Arrests</span>
                        </label>
                        <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${filterHateCrime ? 'bg-red-600/30 border border-red-500' : 'bg-slate-800/50 hover:bg-slate-700/50 border border-transparent'}`}>
                          <Checkbox checked={filterHateCrime} onCheckedChange={(c) => setFilterHateCrime(!!c)} className="hidden" />
                          <span className={`text-sm ${filterHateCrime ? 'text-red-400' : 'text-slate-300'}`}>Hate Crime</span>
                        </label>
                        <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${filterWeapon ? 'bg-red-600/30 border border-red-500' : 'bg-slate-800/50 hover:bg-slate-700/50 border border-transparent'}`}>
                          <Checkbox checked={filterWeapon} onCheckedChange={(c) => setFilterWeapon(!!c)} className="hidden" />
                          <span className={`text-sm ${filterWeapon ? 'text-red-400' : 'text-slate-300'}`}>Weapon</span>
                        </label>
                        <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${filterInjuries ? 'bg-red-600/30 border border-red-500' : 'bg-slate-800/50 hover:bg-slate-700/50 border border-transparent'}`}>
                          <Checkbox checked={filterInjuries} onCheckedChange={(c) => setFilterInjuries(!!c)} className="hidden" />
                          <span className={`text-sm ${filterInjuries ? 'text-red-400' : 'text-slate-300'}`}>Injuries</span>
                        </label>
                        <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${filterMinorElderly ? 'bg-red-600/30 border border-red-500' : 'bg-slate-800/50 hover:bg-slate-700/50 border border-transparent'}`}>
                          <Checkbox checked={filterMinorElderly} onCheckedChange={(c) => setFilterMinorElderly(!!c)} className="hidden" />
                          <span className={`text-sm ${filterMinorElderly ? 'text-red-400' : 'text-slate-300'}`}>Minor/Elderly</span>
                        </label>
                      </div>
                    </div>
                    <div className="bg-purple-950/20 border border-purple-900/30 rounded-lg p-3">
                      <Label className="text-xs text-purple-400 uppercase tracking-wide mb-3 block">Status Flags</Label>
                      <div className="flex flex-wrap gap-2">
                        <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${filterPdActivated ? 'bg-purple-600/30 border border-purple-500' : 'bg-slate-800/50 hover:bg-slate-700/50 border border-transparent'}`}>
                          <Checkbox checked={filterPdActivated} onCheckedChange={(c) => setFilterPdActivated(!!c)} className="hidden" />
                          <span className={`text-sm ${filterPdActivated ? 'text-purple-400' : 'text-slate-300'}`}>PD Activated</span>
                        </label>
                        <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${filterReportFiled ? 'bg-purple-600/30 border border-purple-500' : 'bg-slate-800/50 hover:bg-slate-700/50 border border-transparent'}`}>
                          <Checkbox checked={filterReportFiled} onCheckedChange={(c) => setFilterReportFiled(!!c)} className="hidden" />
                          <span className={`text-sm ${filterReportFiled ? 'text-purple-400' : 'text-slate-300'}`}>Report Filed</span>
                        </label>
                        <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${filterFootageRequested ? 'bg-purple-600/30 border border-purple-500' : 'bg-slate-800/50 hover:bg-slate-700/50 border border-transparent'}`}>
                          <Checkbox checked={filterFootageRequested} onCheckedChange={(c) => setFilterFootageRequested(!!c)} className="hidden" />
                          <span className={`text-sm ${filterFootageRequested ? 'text-purple-400' : 'text-slate-300'}`}>Footage Requested</span>
                        </label>
                        <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${filterFootageObtained ? 'bg-purple-600/30 border border-purple-500' : 'bg-slate-800/50 hover:bg-slate-700/50 border border-transparent'}`}>
                          <Checkbox checked={filterFootageObtained} onCheckedChange={(c) => setFilterFootageObtained(!!c)} className="hidden" />
                          <span className={`text-sm ${filterFootageObtained ? 'text-purple-400' : 'text-slate-300'}`}>Footage Obtained</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Clear + count */}
                  <div className="flex justify-between items-center pt-1">
                    <p className="text-xs text-slate-500">
                      {filteredCalls.length} of {calls.length} calls match
                    </p>
                    {(activeCallFilterCount > 0 || searchPhone) && (
                      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={clearCallFilters}>
                        <X className="h-3 w-3 mr-1" /> Clear All Filters
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Sort indicator (always visible outside filters) */}
          {!showCallFilters && (
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-500">
                {filteredCalls.length} call{filteredCalls.length !== 1 ? "s" : ""}
                {activeCallFilterCount > 0 && <span className="text-blue-400 ml-1">({activeCallFilterCount} filter{activeCallFilterCount !== 1 ? "s" : ""} active)</span>}
              </p>
              <Button
                variant="ghost" size="sm" className="h-7 text-xs text-slate-400"
                onClick={() => setCallSortOrder(callSortOrder === "newest" ? "oldest" : "newest")}
              >
                {callSortOrder === "newest" ? <ArrowDown className="h-3 w-3 mr-1" /> : <ArrowUp className="h-3 w-3 mr-1" />}
                {callSortOrder === "newest" ? "Newest" : "Oldest"}
              </Button>
            </div>
          )}

          {/* Top pagination */}
          {renderPagination()}

          {/* Calls list */}
          {loadingCalls ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : filteredCalls.length === 0 && searchMatchedIncidents.length === 0 && searchMatchedContacts.length === 0 ? (
            <Card className="bg-slate-900 border-slate-700">
              <CardContent className="py-10 text-center text-slate-500">
                No calls found.
              </CardContent>
            </Card>
          ) : filteredCalls.length === 0 ? (
            null
          ) : (
            <Card className="bg-slate-900 border-slate-700">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-700 bg-slate-800/50 text-slate-400 text-xs">
                        <th className="text-left p-2.5 pl-3">Caller</th>
                        <th className="text-left p-2.5 hidden sm:table-cell">Contact</th>
                        <th className="text-left p-2.5 cursor-pointer select-none" onClick={() => setCallSortOrder(callSortOrder === "newest" ? "oldest" : "newest")}>
                          <span className="flex items-center gap-1">
                            Time
                            <ArrowUpDown className="h-3 w-3" />
                          </span>
                        </th>
                        <th className="text-right p-2.5 pr-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedCalls.map((call, i) => (
                        <tr
                          key={call.call_id || i}
                          className="border-b border-slate-800/50 hover:bg-slate-800/40 cursor-pointer transition-colors"
                          onClick={() => openDetail(call.caller_number, call.is_flagged ? call : null)}
                        >
                          <td className="p-2.5 pl-3">
                            <div className="flex items-center gap-1.5 whitespace-nowrap">
                              {call.is_flagged && call.contact_status === "known" ? (
                                <Phone className="h-3 w-3 text-blue-400 shrink-0" />
                              ) : call.is_flagged ? (
                                <Flag className="h-3 w-3 text-yellow-500 shrink-0" />
                              ) : null}
                              <span className="font-mono text-xs sm:text-sm truncate">{call.caller_number}</span>
                              {handlePhoneClick && (
                                <button
                                  className="p-0.5 rounded hover:bg-slate-700 text-green-400 hover:text-green-300 shrink-0"
                                  title="Call this number"
                                  onClick={(e) => { e.stopPropagation(); handlePhoneClick(call.caller_number); }}
                                >
                                  <PhoneOutgoing className="h-3 w-3" />
                                </button>
                              )}
                              {call.has_incidents && (
                                <span className="flex items-center gap-0.5 text-blue-400 shrink-0" title={`${call.incident_count} incident${call.incident_count !== 1 ? "s" : ""}`}>
                                  <FileText className="h-3 w-3" />
                                  <span className="text-[9px]">{call.incident_count}</span>
                                </span>
                              )}
                            </div>
                            {call.contact_name && (
                              <div className="text-[10px] text-slate-400 sm:hidden mt-0.5">{call.contact_name}</div>
                            )}
                          </td>
                          <td className="p-2.5 hidden sm:table-cell">
                            {call.contact_name ? (
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs">{call.contact_name}</span>
                                <Badge className={`text-[9px] px-1.5 py-0 ${STATUS_COLORS[call.contact_status] || STATUS_COLORS.other}`}>
                                  {call.contact_status}
                                </Badge>
                              </div>
                            ) : call.is_flagged ? (
                              <Badge className={`text-[9px] px-1.5 py-0 ${STATUS_COLORS[call.contact_status] || STATUS_COLORS.flagged}`}>
                                {call.contact_status || "flagged"}
                              </Badge>
                            ) : (
                              <span className="text-slate-600">&mdash;</span>
                            )}
                          </td>
                          <td className="p-2.5 whitespace-nowrap text-xs">{formatDateTime(call.received_at)}</td>
                          <td className="p-2.5 pr-3 text-right" onClick={(e) => e.stopPropagation()}>
                            <Button
                              size="sm" variant="ghost"
                              className="h-7 w-7 p-0"
                              onClick={() => openFlag(call.caller_number, call.is_flagged ? call : null)}
                              title={call.is_flagged ? "Edit flag" : "Flag this number"}
                            >
                              {call.is_flagged && call.contact_status === "known" ? <Edit3 className="h-3.5 w-3.5 text-blue-400" /> : call.is_flagged ? <Edit3 className="h-3.5 w-3.5 text-yellow-500" /> : <Flag className="h-3.5 w-3.5 text-slate-500" />}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Bottom pagination */}
          {renderPagination()}

          {/* Matched Incidents from search */}
          {searchMatchedIncidents.length > 0 && (
            <Card className="bg-slate-900 border-slate-700 mt-3">
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-slate-400 uppercase tracking-wide flex items-center gap-1.5">
                    <Shield className="h-3.5 w-3.5" />
                    Linked Incidents ({filteredMatchedIncidents.length}{(matchTypeFilters.length > 0 || callerTypeFilter) ? ` of ${searchMatchedIncidents.length}` : ""})
                  </p>
                  <div className="flex gap-1 items-center">
                    {[
                      { type: "callback", label: "Callback", activeClass: "bg-cyan-600/30 border-cyan-500 text-cyan-300", color: "text-slate-400" },
                      { type: "victim", label: "Victim", activeClass: "bg-emerald-600/30 border-emerald-500 text-emerald-300", color: "text-slate-400" },
                      { type: "witness", label: "Witness", activeClass: "bg-sky-600/30 border-sky-500 text-sky-300", color: "text-slate-400" },
                    ].map(({ type, label, activeClass, color }) => (
                      <button
                        key={type}
                        onClick={() => toggleMatchTypeFilter(type)}
                        className={`text-[10px] px-2 py-0.5 rounded border transition-colors ${
                          matchTypeFilters.includes(type)
                            ? activeClass
                            : `border-slate-700 ${color} hover:border-slate-500`
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                    <span className="text-slate-600 mx-0.5">|</span>
                    <button
                      onClick={() => setCallerTypeFilter(callerTypeFilter === "cv" ? "" : "cv")}
                      className={`text-[10px] px-2 py-0.5 rounded border transition-colors ${
                        callerTypeFilter === "cv"
                          ? "bg-cyan-600/30 border-cyan-500 text-cyan-300"
                          : "border-slate-700 text-slate-400 hover:border-slate-500"
                      }`}
                    >
                      CV
                    </button>
                    <button
                      onClick={() => setCallerTypeFilter(callerTypeFilter === "3rd_party" ? "" : "3rd_party")}
                      className={`text-[10px] px-2 py-0.5 rounded border transition-colors ${
                        callerTypeFilter === "3rd_party"
                          ? "bg-indigo-600/30 border-indigo-500 text-indigo-300"
                          : "border-slate-700 text-slate-400 hover:border-slate-500"
                      }`}
                    >
                      3rd Party
                    </button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  {filteredMatchedIncidents.map((inc) => (
                    <div
                      key={inc.incident_id}
                      className="p-2 bg-slate-800/50 rounded hover:bg-slate-800 transition-colors"
                    >
                      <div className="flex items-center justify-between cursor-pointer" onClick={() => navigate(`/incidents/${inc.incident_id}`)}>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium truncate">{inc.headline || inc.incident_type || "Incident"}</span>
                            {inc.callback_caller_type && (
                              <Badge className={`text-[10px] px-1.5 py-0 ${inc.callback_caller_type === "cv" ? "bg-cyan-700" : "bg-indigo-700"}`}>
                                {inc.callback_caller_type === "cv" ? "CV" : "3RD PARTY"}
                              </Badge>
                            )}
                            {inc.callback_has_visual && <Badge className="text-[10px] px-1.5 py-0 bg-green-700">VISUAL</Badge>}
                            {inc.has_arrest && <Badge className="bg-red-900/50 text-red-300 text-[10px] px-1.5 py-0">ARREST</Badge>}
                            {inc.has_ready && <Badge className="bg-amber-900/50 text-amber-300 text-[10px] px-1.5 py-0">READY</Badge>}
                            {inc.match_types?.includes("callback") && <Badge className="text-[10px] px-1.5 py-0 bg-cyan-700 text-white">CALLBACK</Badge>}
                            {inc.match_types?.includes("victim") && <Badge className="text-[10px] px-1.5 py-0 bg-emerald-700 text-white">VICTIM</Badge>}
                            {inc.match_types?.includes("witness") && <Badge className="text-[10px] px-1.5 py-0 bg-sky-700 text-white">WITNESS</Badge>}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                            {inc.date_time && <span>{new Date(inc.date_time).toLocaleDateString("en-US", { timeZone: "America/New_York" })}</span>}
                            {inc.address && <span className="truncate">{inc.address}</span>}
                          </div>
                        </div>
                        <Badge variant="outline" className="text-xs shrink-0 ml-2">{inc.status?.replace("_", " ") || "open"}</Badge>
                      </div>
                      {(inc.matched_phone || searchPhone) && (
                        <button
                          className="text-xs text-blue-400 hover:underline mt-1 flex items-center gap-1"
                          onClick={(e) => { e.stopPropagation(); openDetail((inc.matched_phone || searchPhone).replace(/\D/g, '')); }}
                        >
                          <Phone className="w-3 h-3" />
                          {inc.matched_phone || searchPhone} — View History
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Matched Contacts from search */}
          {searchMatchedContacts.length > 0 && (
            <Card className="bg-slate-900 border-slate-700 mt-3">
              <CardContent className="p-3">
                <p className="text-xs text-slate-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <Phone className="h-3.5 w-3.5" />
                  Matching Contacts ({searchMatchedContacts.length})
                </p>
                <div className="space-y-1.5">
                  {searchMatchedContacts.map((c) => (
                    <div
                      key={c.contact_id}
                      className="flex items-center justify-between p-2 bg-slate-800/50 rounded hover:bg-slate-800 cursor-pointer transition-colors"
                      onClick={() => openDetail(c.phone_number, c)}
                    >
                      <div className="min-w-0">
                        <span className="font-mono text-sm">{c.phone_number}</span>
                        {c.name && <span className="text-sm text-slate-400 ml-2">{c.name}</span>}
                      </div>
                      <Badge className={`text-[10px] px-1.5 py-0 shrink-0 ml-2 ${c.status === "known" ? "bg-blue-900/50 text-blue-300" : "bg-yellow-900/50 text-yellow-300"}`}>
                        {c.status || "flagged"}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* =========== DISPATCH TAB =========== */}
      {activeTab === "dispatch" && (
        <>
          {/* Search & Filters */}
          <Card className="bg-slate-900 border-slate-700">
            <CardContent className="pt-3 pb-3">
              <div className="flex gap-2 items-center flex-wrap">
                <div className="flex-1 min-w-[140px]">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-500" />
                    <Input
                      placeholder="Phone number or user..."
                      value={dispatchSearch}
                      onChange={(e) => setDispatchSearch(e.target.value)}
                      className="pl-8 h-9 text-sm bg-slate-800 border-slate-700"
                    />
                  </div>
                </div>
                <Select value={dispatchFilterType} onValueChange={setDispatchFilterType}>
                  <SelectTrigger className="w-[100px] h-9 text-xs bg-slate-800 border-slate-700">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="browser">Browser</SelectItem>
                    <SelectItem value="call_my_phone">Phone</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={dispatchFilterStatus} onValueChange={setDispatchFilterStatus}>
                  <SelectTrigger className="w-[110px] h-9 text-xs bg-slate-800 border-slate-700">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="no-answer">No Answer</SelectItem>
                    <SelectItem value="busy">Busy</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                    <SelectItem value="canceled">Canceled</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={dispatchFilterUser} onValueChange={setDispatchFilterUser}>
                  <SelectTrigger className="w-[120px] h-9 text-xs bg-slate-800 border-slate-700">
                    <SelectValue placeholder="User" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    {[...new Set(dispatchCalls.map(c => c.user_name).filter(Boolean))].sort().map(name => (
                      <SelectItem key={name} value={name}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {(dispatchSearch || (dispatchFilterType && dispatchFilterType !== "all") || (dispatchFilterStatus && dispatchFilterStatus !== "all") || (dispatchFilterUser && dispatchFilterUser !== "all")) && (
                  <Button variant="ghost" size="sm" className="h-9 px-2 text-xs text-slate-400" onClick={() => { setDispatchSearch(""); setDispatchFilterType(""); setDispatchFilterStatus(""); setDispatchFilterUser(""); }}>
                    <X className="w-3 h-3 mr-1" />Clear
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {(() => {
            const q = dispatchSearch.toLowerCase().trim();
            const filtered = dispatchCalls.filter(call => {
              if (q && !(call.to_number || "").toLowerCase().includes(q) && !(call.user_name || "").toLowerCase().includes(q)) return false;
              if (dispatchFilterType && dispatchFilterType !== "all" && call.call_type !== dispatchFilterType) return false;
              if (dispatchFilterStatus && dispatchFilterStatus !== "all" && call.status !== dispatchFilterStatus) return false;
              if (dispatchFilterUser && dispatchFilterUser !== "all" && call.user_name !== dispatchFilterUser) return false;
              return true;
            });
            return <>
              <div className="flex items-center justify-between">
                <p className="text-xs text-slate-500">
                  {filtered.length}{filtered.length !== dispatchCalls.length ? ` of ${dispatchCalls.length}` : ""} dispatch call{filtered.length !== 1 ? "s" : ""}
                </p>
              </div>

          {loadingDispatch ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : filtered.length === 0 ? (
            <Card className="bg-slate-900 border-slate-700">
              <CardContent className="py-10 text-center text-slate-500">
                <PhoneOutgoing className="h-10 w-10 mx-auto mb-3 opacity-50" />
                <p>{dispatchCalls.length === 0 ? "No dispatch calls yet." : "No calls match your filters."}</p>
                {dispatchCalls.length === 0 && <p className="text-xs mt-1">Outgoing calls from the browser dialer will appear here.</p>}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {filtered.map((call) => (
                <Card key={call.call_sid} className={`bg-slate-900 border-slate-700 ${call.status === "initiated" || call.status === "ringing" || call.status === "in-progress" ? "border-l-2 border-l-green-500" : ""}`}>
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <PhoneOutgoing className={`h-3.5 w-3.5 shrink-0 ${call.status === "initiated" || call.status === "ringing" || call.status === "in-progress" ? "text-green-400 animate-pulse" : "text-blue-400"}`} />
                          <span
                            className="font-mono text-sm text-blue-400 hover:text-blue-300 cursor-pointer hover:underline"
                            onClick={() => openDetail(call.to_number)}
                          >{call.to_number}</span>
                          {handlePhoneClick && (
                            <button
                              className="p-0.5 rounded hover:bg-slate-700 text-green-400 hover:text-green-300 shrink-0"
                              title="Call this number"
                              onClick={(e) => { e.stopPropagation(); handlePhoneClick(call.to_number); }}
                            >
                              <PhoneOutgoing className="h-3 w-3" />
                            </button>
                          )}
                          <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${
                            call.status === "completed" ? "border-green-600 text-green-400" :
                            call.status === "initiated" || call.status === "ringing" || call.status === "in-progress" ? "border-green-500 text-green-400 animate-pulse" :
                            call.status === "no-answer" ? "border-yellow-600 text-yellow-400" :
                            call.status === "busy" ? "border-orange-600 text-orange-400" :
                            call.status === "failed" || call.status === "canceled" ? "border-red-600 text-red-400" :
                            ""
                          }`}>
                            {call.status === "initiated" ? "active" : call.status}
                          </Badge>
                          {user?.role === "admin" &&
                            (call.status === "initiated" || call.status === "ringing" || call.status === "in-progress") && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-5 px-1.5 text-[9px] border-red-600 text-red-400 hover:bg-red-900/30"
                              onClick={(e) => { e.stopPropagation(); forceHangup(call.call_sid); }}
                              title="Force this stuck call to completed"
                            >
                              <PhoneOff className="h-3 w-3 mr-0.5" />Force Hangup
                            </Button>
                          )}
                          {call.call_type && (
                            <Badge className={`text-[9px] px-1.5 py-0 ${call.call_type === "call_my_phone" ? "bg-purple-700" : "bg-slate-700"}`}>
                              {call.call_type === "call_my_phone" ? "MY PHONE" : "BROWSER"}
                            </Badge>
                          )}
                          {call.caller_id === "+17182221800" && (
                            <Badge className="text-[9px] px-1.5 py-0 bg-amber-600 text-black font-bold">
                              CHAVERIM
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-xs text-slate-400 flex-wrap">
                          <span>{formatDateTime(call.started_at)}</span>
                          {call.duration > 0 && (
                            <span>&bull; {formatDuration(call.duration)}</span>
                          )}
                          {call.user_name && (
                            <span>&bull; <span className="text-slate-300">{call.user_name}</span></span>
                          )}
                        </div>
                      </div>
                      {/* Recording toggle */}
                      <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
                        {call.recording_playback_url ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            className={`h-8 w-8 p-0 ${expandedRecording === call.call_sid ? "text-green-400" : "text-slate-400"}`}
                            onClick={() => {
                              if (expandedRecording === call.call_sid) {
                                // Collapse and stop
                                if (audioRef.current) { audioRef.current.pause(); }
                                setPlayingRecording(null);
                                setExpandedRecording(null);
                                setRecordingProgress(0);
                                setRecordingDuration(0);
                              } else {
                                // Stop any current playback
                                if (audioRef.current) { audioRef.current.pause(); }
                                setPlayingRecording(null);
                                setRecordingProgress(0);
                                setRecordingDuration(0);
                                setExpandedRecording(call.call_sid);
                              }
                            }}
                            title={expandedRecording === call.call_sid ? "Close player" : "Open player"}
                          >
                            <Play className="h-4 w-4" />
                          </Button>
                        ) : (
                          <span className="text-[10px] text-slate-600 px-1">No recording</span>
                        )}
                      </div>
                    </div>
                    {/* Expanded recording player */}
                    {expandedRecording === call.call_sid && call.recording_playback_url && (
                      <div className="mt-2 pt-2 border-t border-slate-800" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            className={`h-7 w-7 p-0 shrink-0 ${playingRecording === call.call_sid ? "text-green-400" : "text-slate-400"}`}
                            onClick={async () => {
                              if (playingRecording === call.call_sid) {
                                audioRef.current?.pause();
                                setPlayingRecording(null);
                                return;
                              }
                              // If we already have blob URL, reuse it
                              let blobUrl = recordingBlobUrls[call.call_sid];
                              if (!blobUrl) {
                                try {
                                  const resp = await axios.get(`${API}${call.recording_playback_url}`, {
                                    withCredentials: true,
                                    responseType: "blob",
                                  });
                                  blobUrl = URL.createObjectURL(resp.data);
                                  setRecordingBlobUrls(prev => ({ ...prev, [call.call_sid]: blobUrl }));
                                } catch (err) {
                                  console.error("Failed to load recording:", err);
                                  toast.error("Failed to load recording");
                                  return;
                                }
                              }
                              if (audioRef.current) { audioRef.current.pause(); }
                              const audio = new Audio(blobUrl);
                              audio.ontimeupdate = () => {
                                if (audio.duration) setRecordingProgress(audio.currentTime / audio.duration);
                              };
                              audio.onloadedmetadata = () => setRecordingDuration(audio.duration);
                              audio.onended = () => { setPlayingRecording(null); setRecordingProgress(1); };
                              audioRef.current = audio;
                              setPlayingRecording(call.call_sid);
                              audio.play();
                            }}
                          >
                            {playingRecording === call.call_sid ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                          </Button>
                          {/* Seek bar */}
                          <input
                            type="range"
                            min="0"
                            max="1000"
                            value={playingRecording === call.call_sid || recordingProgress > 0 ? Math.round(recordingProgress * 1000) : 0}
                            onChange={(e) => {
                              const val = parseInt(e.target.value) / 1000;
                              setRecordingProgress(val);
                              if (audioRef.current && audioRef.current.duration) {
                                audioRef.current.currentTime = val * audioRef.current.duration;
                              }
                            }}
                            className="flex-1 h-1 accent-green-500 cursor-pointer"
                          />
                          {/* Duration display */}
                          <span className="text-[10px] text-slate-500 shrink-0 w-10 text-right font-mono">
                            {recordingDuration > 0 ? formatDuration(Math.round(
                              playingRecording === call.call_sid && audioRef.current
                                ? audioRef.current.currentTime
                                : recordingProgress * recordingDuration
                            )) : call.duration > 0 ? formatDuration(call.duration) : "--:--"}
                          </span>
                          {/* Download button */}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 text-slate-400 hover:text-blue-400 shrink-0"
                            onClick={async () => {
                              try {
                                let blobUrl = recordingBlobUrls[call.call_sid];
                                let blob;
                                if (blobUrl) {
                                  const resp = await fetch(blobUrl);
                                  blob = await resp.blob();
                                } else {
                                  const resp = await axios.get(`${API}${call.recording_playback_url}`, {
                                    withCredentials: true,
                                    responseType: "blob",
                                  });
                                  blob = resp.data;
                                  blobUrl = URL.createObjectURL(blob);
                                  setRecordingBlobUrls(prev => ({ ...prev, [call.call_sid]: blobUrl }));
                                }
                                const a = document.createElement("a");
                                a.href = URL.createObjectURL(blob);
                                a.download = `recording_${call.to_number || "unknown"}_${call.call_sid}.mp3`;
                                a.click();
                                URL.revokeObjectURL(a.href);
                              } catch (err) {
                                console.error("Failed to download:", err);
                                toast.error("Failed to download recording");
                              }
                            }}
                            title="Download recording"
                          >
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
            </>;
          })()}
        </>
      )}

      {/* =========== CONTACTS TAB =========== */}
      {activeTab === "contacts" && (
        <>
          {/* Search bar + Filters toggle + Add Contact */}
          <Card className="bg-slate-900 border-slate-700">
            <CardContent className="pt-3 pb-3">
              <div className="flex gap-2 items-center flex-wrap">
                <div className="flex-1 min-w-[120px]">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-500" />
                    <Input
                      placeholder="Phone, name, notes..."
                      value={contactSearch}
                      onChange={(e) => setContactSearch(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && applyContactSearch()}
                      className="pl-8 h-9 text-sm bg-slate-800 border-slate-700"
                    />
                  </div>
                </div>
                <div className="flex gap-1.5 items-center">
                  <Button variant="outline" size="sm" className="h-9 px-2" onClick={() => setShowContactFilters(!showContactFilters)}>
                    <Filter className="h-3.5 w-3.5" />
                    {activeContactFilterCount > 0 && (
                      <Badge className="ml-1 h-4 w-4 p-0 flex items-center justify-center text-[9px] bg-blue-600">{activeContactFilterCount}</Badge>
                    )}
                  </Button>
                  <Button onClick={applyContactSearch} size="sm" className="h-9 px-3">
                    <Search className="h-3.5 w-3.5" />
                  </Button>
                  <Button onClick={openNewContact} size="sm" className="h-9 px-2 bg-green-700 hover:bg-green-600">
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>

              {/* Expandable filter panel */}
              {showContactFilters && (
                <div className="mt-3 pt-3 border-t border-slate-800 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="bg-slate-800/50 rounded-lg p-3">
                      <Label className="text-xs text-slate-400 uppercase tracking-wide mb-2 block">Contact Status</Label>
                      <Select value={contactFilterStatus || "all"} onValueChange={(v) => setContactFilterStatus(v === "all" ? "" : v)}>
                        <SelectTrigger className="h-9 bg-slate-900 border-slate-700">
                          <SelectValue placeholder="All" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Statuses</SelectItem>
                          {CONTACT_STATUSES.map(s => (
                            <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-3">
                      <Label className="text-xs text-slate-400 uppercase tracking-wide mb-2 block">Sort By</Label>
                      <Select value={contactSortOrder} onValueChange={setContactSortOrder}>
                        <SelectTrigger className="h-9 bg-slate-900 border-slate-700">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="newest">Newest First</SelectItem>
                          <SelectItem value="oldest">Oldest First</SelectItem>
                          <SelectItem value="name">Name (A-Z)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Clear + count */}
                  <div className="flex justify-between items-center pt-1">
                    <p className="text-xs text-slate-500">
                      {filteredContacts.length} of {contacts.length} contacts match
                    </p>
                    {(activeContactFilterCount > 0 || contactSearch) && (
                      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={clearContactFilters}>
                        <X className="h-3 w-3 mr-1" /> Clear All Filters
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Sort indicator (always visible outside filters) */}
          {!showContactFilters && (
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-500">
                {filteredContacts.length} contact{filteredContacts.length !== 1 ? "s" : ""}
                {activeContactFilterCount > 0 && <span className="text-blue-400 ml-1">({activeContactFilterCount} filter{activeContactFilterCount !== 1 ? "s" : ""} active)</span>}
              </p>
              <Button
                variant="ghost" size="sm" className="h-7 text-xs text-slate-400"
                onClick={() => {
                  const orders = ["newest", "oldest", "name"];
                  const idx = orders.indexOf(contactSortOrder);
                  setContactSortOrder(orders[(idx + 1) % orders.length]);
                }}
              >
                <ArrowUpDown className="h-3 w-3 mr-1" />
                {contactSortOrder === "newest" ? "Newest" : contactSortOrder === "oldest" ? "Oldest" : "Name"}
              </Button>
            </div>
          )}

          {/* Contacts list */}
          {loadingContacts ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : filteredContacts.length === 0 ? (
            <Card className="bg-slate-900 border-slate-700">
              <CardContent className="py-10 text-center text-slate-500">
                <p>No contacts found.</p>
                <Button onClick={openNewContact} variant="outline" size="sm" className="mt-3">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add Contact
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {filteredContacts.map((contact) => (
                <Card
                  key={contact.contact_id}
                  className="bg-slate-900 border-slate-700 hover:bg-slate-800/50 cursor-pointer transition-colors"
                  onClick={() => openDetail(contact.phone_number, contact)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Flag className="h-3.5 w-3.5 text-yellow-500 shrink-0" />
                          <span className="font-mono text-sm">{contact.phone_number}</span>
                          {handlePhoneClick && (
                            <button
                              className="p-0.5 rounded hover:bg-slate-700 text-green-400 hover:text-green-300 shrink-0"
                              title="Call this number"
                              onClick={(e) => { e.stopPropagation(); handlePhoneClick(contact.phone_number); }}
                            >
                              <PhoneOutgoing className="h-3.5 w-3.5" />
                            </button>
                          )}
                          <Badge className={`text-[9px] px-1.5 py-0 ${STATUS_COLORS[contact.status] || STATUS_COLORS.other}`}>
                            {contact.status}
                          </Badge>
                          {contact.incident_count > 0 && (
                            <Badge variant="outline" className="text-[9px] px-1.5 py-0">
                              {contact.incident_count} incident{contact.incident_count !== 1 ? "s" : ""}
                            </Badge>
                          )}
                        </div>
                        {contact.name && (
                          <div className="text-sm mt-1 text-slate-300">{contact.name}</div>
                        )}
                        {contact.notes && (
                          <div className="text-xs text-slate-500 mt-0.5 truncate">{contact.notes}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openFlag(contact.phone_number, contact)} title="Edit">
                          <Edit3 className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400 hover:text-red-300" onClick={() => deleteFlag(contact.contact_id)} title="Remove flag">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* =========== ALL NUMBERS TAB =========== */}
      {activeTab === "numbers" && (
        <>
          {/* Search + Filters */}
          <Card className="bg-slate-900 border-slate-700">
            <CardContent className="pt-3 pb-3">
              <div className="flex gap-2 items-center flex-wrap">
                <div className="flex-1 min-w-[140px]">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-500" />
                    <Input
                      placeholder="Search phone or name..."
                      value={allNumSearch}
                      onChange={(e) => setAllNumSearch(e.target.value)}
                      className="pl-8 h-9 text-sm bg-slate-800 border-slate-700"
                    />
                  </div>
                </div>
                <Select value={allNumSort} onValueChange={setAllNumSort}>
                  <SelectTrigger className="h-9 w-40 bg-slate-800 border-slate-700 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="incidents">Most Incidents</SelectItem>
                    <SelectItem value="newest">Newest First</SelectItem>
                    <SelectItem value="oldest">Oldest First</SelectItem>
                    <SelectItem value="phone">Phone Number</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {/* Filter toggles */}
              <div className="flex gap-1 items-center flex-wrap mt-2">
                <span className="text-xs text-slate-500 mr-1">Type:</span>
                {[
                  { type: "callback", label: "Callback", activeClass: "bg-cyan-600/30 border-cyan-500 text-cyan-300" },
                  { type: "victim", label: "Victim", activeClass: "bg-emerald-600/30 border-emerald-500 text-emerald-300" },
                  { type: "witness", label: "Witness", activeClass: "bg-sky-600/30 border-sky-500 text-sky-300" },
                ].map(({ type, label, activeClass }) => (
                  <button
                    key={type}
                    onClick={() => toggleAllNumMatchFilter(type)}
                    className={`text-[10px] px-2 py-0.5 rounded border transition-colors ${
                      allNumMatchFilter.includes(type) ? activeClass : "border-slate-700 text-slate-400 hover:border-slate-500"
                    }`}
                  >
                    {label}
                  </button>
                ))}
                <span className="text-slate-600 mx-0.5">|</span>
                <button
                  onClick={() => setAllNumCallerFilter(allNumCallerFilter === "cv" ? "" : "cv")}
                  className={`text-[10px] px-2 py-0.5 rounded border transition-colors ${
                    allNumCallerFilter === "cv" ? "bg-cyan-600/30 border-cyan-500 text-cyan-300" : "border-slate-700 text-slate-400 hover:border-slate-500"
                  }`}
                >
                  CV
                </button>
                <button
                  onClick={() => setAllNumCallerFilter(allNumCallerFilter === "3rd_party" ? "" : "3rd_party")}
                  className={`text-[10px] px-2 py-0.5 rounded border transition-colors ${
                    allNumCallerFilter === "3rd_party" ? "bg-indigo-600/30 border-indigo-500 text-indigo-300" : "border-slate-700 text-slate-400 hover:border-slate-500"
                  }`}
                >
                  3rd Party
                </button>
                <span className="text-slate-600 mx-0.5">|</span>
                <button
                  onClick={() => setAllNumFlaggedOnly(!allNumFlaggedOnly)}
                  className={`text-[10px] px-2 py-0.5 rounded border transition-colors ${
                    allNumFlaggedOnly ? "bg-yellow-600/30 border-yellow-500 text-yellow-300" : "border-slate-700 text-slate-400 hover:border-slate-500"
                  }`}
                >
                  Flagged
                </button>
                <button
                  onClick={() => setAllNumArrestOnly(!allNumArrestOnly)}
                  className={`text-[10px] px-2 py-0.5 rounded border transition-colors ${
                    allNumArrestOnly ? "bg-red-600/30 border-red-500 text-red-300" : "border-slate-700 text-slate-400 hover:border-slate-500"
                  }`}
                >
                  Arrest
                </button>
                <span className="text-xs text-slate-500 ml-auto">{filteredAllNumbers.length} number{filteredAllNumbers.length !== 1 ? "s" : ""}</span>
              </div>
            </CardContent>
          </Card>

          {/* Numbers list */}
          {loadingAllNumbers ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : filteredAllNumbers.length === 0 ? (
            <Card className="bg-slate-900 border-slate-700">
              <CardContent className="py-10 text-center text-slate-500">
                No numbers found.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-1">
              {filteredAllNumbers.map((num) => (
                <Card
                  key={num.phone_normalized}
                  className="bg-slate-900 border-slate-700 cursor-pointer hover:bg-slate-800/80 transition-colors"
                  onClick={() => openDetail(num.phone_normalized)}
                >
                  <CardContent className="py-2 px-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-sm font-medium">{num.phone}</span>
                          {num.is_flagged && (
                            <Badge className={`text-[9px] px-1.5 py-0 ${num.contact_status === "known" ? "bg-blue-900/50 text-blue-300" : "bg-yellow-900/50 text-yellow-300"}`}>
                              {num.contact_status || "flagged"}
                            </Badge>
                          )}
                          {num.match_types?.includes("callback") && <Badge className="text-[9px] px-1 py-0 bg-cyan-700/60 text-cyan-200">CALLBACK</Badge>}
                          {num.match_types?.includes("victim") && <Badge className="text-[9px] px-1 py-0 bg-emerald-700/60 text-emerald-200">VICTIM</Badge>}
                          {num.match_types?.includes("witness") && <Badge className="text-[9px] px-1 py-0 bg-sky-700/60 text-sky-200">WITNESS</Badge>}
                          {num.caller_types?.includes("cv") && <Badge className="text-[9px] px-1 py-0 bg-cyan-800/50 text-cyan-300">CV</Badge>}
                          {num.caller_types?.some(t => t && t !== "cv") && <Badge className="text-[9px] px-1 py-0 bg-indigo-800/50 text-indigo-300">3RD</Badge>}
                          {num.has_visual && <Badge className="text-[9px] px-1 py-0 bg-green-800/50 text-green-300">VISUAL</Badge>}
                          {num.has_arrest && <Badge className="text-[9px] px-1 py-0 bg-red-800/50 text-red-300">ARREST</Badge>}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5 flex-wrap">
                          <span>{num.incident_count} incident{num.incident_count !== 1 ? "s" : ""}</span>
                          {num.latest_date && <span>&bull; {new Date(num.latest_date).toLocaleDateString("en-US", { timeZone: "America/New_York" })}</span>}
                          {num.names?.length > 0 && <span>&bull; {num.names.slice(0, 2).join(", ")}{num.names.length > 2 ? ` +${num.names.length - 2}` : ""}</span>}
                          {num.contact_name && <span>&bull; {num.contact_name}</span>}
                          {num.incident_types?.length > 0 && <span className="text-slate-600">&bull; {num.incident_types.slice(0, 3).join(", ")}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Badge variant="outline" className="text-xs font-mono">{num.incident_count}</Badge>
                        <ChevronRight className="w-4 h-4 text-slate-500" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* =========== CHAVERIM TAB =========== */}
      {activeTab === "chaverim" && (
        <>
          <Card className="bg-slate-900 border-slate-700">
            <CardContent className="pt-3 pb-3">
              <div className="flex items-center gap-2">
                <Search className="h-4 w-4 text-slate-400 shrink-0" />
                <Input
                  value={chaverimSearch}
                  onChange={(e) => setChaverimSearch(e.target.value)}
                  placeholder="Search phone or name..."
                  className="input-dark h-8"
                />
              </div>
              <p className="text-[11px] text-slate-500 mt-2">
                Every call to the Chaverim alert line ((929) 828-6444) is logged here. Newest first.
              </p>
            </CardContent>
          </Card>

          {loadingChaverim ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : chaverimCalls.length === 0 ? (
            <div className="text-center py-8 text-sm text-slate-500">
              No Chaverim line calls yet.
            </div>
          ) : (
            <div className="space-y-1.5">
              {chaverimCalls
                .filter((c) => {
                  const q = chaverimSearch.trim().toLowerCase();
                  if (!q) return true;
                  const digits = (c.caller_number || "").replace(/\D/g, "");
                  return (
                    (c.caller_number || "").toLowerCase().includes(q) ||
                    (c.caller_name || "").toLowerCase().includes(q) ||
                    (c.contact_name || "").toLowerCase().includes(q) ||
                    digits.includes(q.replace(/\D/g, ""))
                  );
                })
                .map((c) => {
                  const displayNumber = (() => {
                    const d = (c.caller_number || "").replace(/\D/g, "");
                    const core = d.length === 11 && d.startsWith("1") ? d.slice(1) : d;
                    if (core.length === 10) return `(${core.slice(0, 3)}) ${core.slice(3, 6)}-${core.slice(6)}`;
                    return c.caller_number || "Unknown";
                  })();
                  return (
                    <div key={c.call_id} className="bg-slate-800 rounded-lg px-3 py-2 flex items-center gap-3">
                      <PhoneIncoming className="h-4 w-4 text-amber-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-sm">
                          <span className="font-medium truncate">{displayNumber}</span>
                          {c.contact_name && (
                            <Badge variant="outline" className="text-[9px] px-1 py-0 border-emerald-600 text-emerald-400">
                              {c.contact_name}
                            </Badge>
                          )}
                          {c.caller_name && (
                            <span className="text-xs text-slate-400 truncate">{c.caller_name}</span>
                          )}
                          {handlePhoneClick && c.caller_number && (
                            <button
                              className="p-0.5 rounded hover:bg-slate-700 text-green-400 hover:text-green-300 shrink-0"
                              title="Call this number"
                              onClick={(e) => { e.stopPropagation(); handlePhoneClick(c.caller_number); }}
                            >
                              <PhoneOutgoing className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">{formatDateTime(c.received_at)}</div>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </>
      )}

      {/* =========== CALL DETAIL MODAL =========== */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-lg w-[calc(100vw-2rem)] max-h-[85vh] overflow-y-auto overflow-x-hidden bg-slate-900 border-slate-700 [&>*]:min-w-0">
          <DialogHeader className="min-w-0">
            <DialogTitle className="flex items-center gap-2 min-w-0">
              <Phone className="h-5 w-5 shrink-0" />
              <span className="truncate">{detailPhone}</span>
            </DialogTitle>
          </DialogHeader>

          {/* Contact info */}
          {detailContact && (detailContact.is_flagged || detailContact.contact_id) ? (
            <div className="bg-slate-800 rounded-lg p-3 space-y-2 min-w-0">
              <div className="flex items-center justify-between gap-1">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <Flag className="h-4 w-4 text-yellow-500 shrink-0" />
                  <span className="text-sm font-medium whitespace-nowrap">Flagged</span>
                  <Badge className={`text-[9px] px-1.5 py-0 ${STATUS_COLORS[detailContact.contact_status || detailContact.status] || STATUS_COLORS.flagged}`}>
                    {detailContact.contact_status || detailContact.status || "flagged"}
                  </Badge>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => openFlag(detailPhone, detailContact)}>
                    <Edit3 className="h-3 w-3 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-red-400" onClick={() => deleteFlag(detailContact.contact_id)}>
                    <Trash2 className="h-3 w-3 mr-1" /> Unflag
                  </Button>
                </div>
              </div>
              {(detailContact.contact_name || detailContact.name) && (
                <div className="text-sm"><span className="text-slate-400">Name:</span> {detailContact.contact_name || detailContact.name}</div>
              )}
              {(detailContact.contact_notes || detailContact.notes) && (
                <div className="text-xs text-slate-400">{detailContact.contact_notes || detailContact.notes}</div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-between bg-slate-800 rounded-lg p-3">
              <span className="text-sm text-slate-400">Not flagged</span>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openFlag(detailPhone)}>
                <Flag className="h-3 w-3 mr-1" /> Flag Number
              </Button>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              className="flex-1 h-8 text-xs"
              onClick={() => {
                setDetailOpen(false);
                navigate(`/live-calls?callback=${encodeURIComponent(detailPhone)}`);
              }}
            >
              <Phone className="w-3 h-3 mr-1" />
              Open Live Call
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="flex-1 h-8 text-xs"
              onClick={() => {
                setDetailOpen(false);
                navigate(`/incidents?new=true&callback=${encodeURIComponent(detailPhone)}`);
              }}
            >
              <FileText className="w-3 h-3 mr-1" />
              Create Incident
            </Button>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              className="flex-1 h-8 text-xs"
              onClick={() => {
                setLinkSearch("");
                setLinkResults([]);
                setLinkCallerType("");
                setLinkHasVisual(false);
                setLinkIncidentOpen(true);
              }}
            >
              <Link2 className="w-3 h-3 mr-1" />
              Link to Incident
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="flex-1 h-8 text-xs"
              onClick={() => {
                setLiveCallSearch("");
                setLinkCallerType("");
                setLinkHasVisual(false);
                setLinkLiveCallOpen(true);
                fetchActiveLiveCalls();
              }}
            >
              <Phone className="w-3 h-3 mr-1" />
              Add to Live Call
            </Button>
          </div>

          {/* Tabs: Linked Incidents | Call History */}
          <div className="flex gap-1 border-b border-slate-700 min-w-0">
            <button
              className={`px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                detailTab === "incidents"
                  ? "border-blue-500 text-blue-400"
                  : "border-transparent text-slate-400 hover:text-slate-300"
              }`}
              onClick={() => setDetailTab("incidents")}
            >
              <FileText className="h-3.5 w-3.5 inline mr-1" />
              Linked Incidents
              {detailIncidents.length > 0 && (
                <span className="ml-1 text-[10px] bg-slate-700 rounded-full px-1.5">{detailIncidents.length}</span>
              )}
            </button>
            <button
              className={`px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                detailTab === "history"
                  ? "border-blue-500 text-blue-400"
                  : "border-transparent text-slate-400 hover:text-slate-300"
              }`}
              onClick={() => setDetailTab("history")}
            >
              <Phone className="h-3.5 w-3.5 inline mr-1" />
              Call History
              {detailCallHistory.length > 0 && (
                <span className="ml-1 text-[10px] bg-slate-700 rounded-full px-1.5">{detailCallHistory.length}</span>
              )}
            </button>
          </div>

          {/* Tab content: Linked Incidents */}
          {detailTab === "incidents" && (
            <div className="min-w-0 overflow-hidden">
              {loadingIncidents ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                </div>
              ) : detailIncidents.length === 0 ? (
                <div className="text-center py-4 text-sm text-slate-500">
                  No incidents linked to this number.
                </div>
              ) : (
                <div className="space-y-2">
                  {detailIncidents.map((inc) => (
                    <div
                      key={inc.incident_id}
                      className="bg-slate-800 rounded-lg p-3 hover:bg-slate-700/50 cursor-pointer transition-colors"
                      onClick={() => { setDetailOpen(false); navigate(`/incidents/${inc.incident_id}`); }}
                    >
                      <div className="min-w-0">
                        <div className="min-w-0">
                          <div className="text-sm font-medium break-words">{inc.headline || "No headline"}</div>
                          <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                            {inc.callback_caller_type && (
                              <Badge className={`text-[9px] px-1.5 py-0 ${inc.callback_caller_type === "cv" ? "bg-cyan-700" : "bg-indigo-700"}`}>
                                {inc.callback_caller_type === "cv" ? "CV" : "3RD PARTY"}
                              </Badge>
                            )}
                            {inc.callback_has_visual && (
                              <Badge className="text-[9px] px-1.5 py-0 bg-green-700">VISUAL</Badge>
                            )}
                            {inc.has_arrest && (
                              <Badge className="text-[9px] px-1.5 py-0 bg-red-600">ARREST</Badge>
                            )}
                            {inc.has_ready && (
                              <Badge className="text-[9px] px-1.5 py-0 bg-amber-600">READY</Badge>
                            )}
                            {inc.match_types?.includes("victim") && (
                              <Badge className="text-[9px] px-1.5 py-0 bg-emerald-700">VICTIM</Badge>
                            )}
                            {inc.match_types?.includes("witness") && (
                              <Badge className="text-[9px] px-1.5 py-0 bg-sky-700">WITNESS</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-xs text-slate-400 flex-wrap">
                            <span>{formatDateOnly(inc.date_time)}</span>
                            {inc.incident_type && <span>&bull; {inc.incident_type}</span>}
                            {inc.status && (
                              <Badge variant="outline" className="text-[9px] px-1 py-0">{inc.status}</Badge>
                            )}
                            {inc.priority && (
                              <Badge variant="outline" className={`text-[9px] px-1 py-0 ${
                                inc.priority === "critical" ? "border-red-500 text-red-400" :
                                inc.priority === "high" ? "border-orange-500 text-orange-400" :
                                ""
                              }`}>{inc.priority}</Badge>
                            )}
                          </div>
                          {inc.address && (
                            <div className="text-xs text-slate-500 mt-0.5 break-words">{inc.address}</div>
                          )}
                          {inc.victims?.length > 0 && (
                            <div className="text-[10px] text-emerald-400/80 mt-0.5">
                              <span className="text-slate-500">V:</span> {inc.victims.map(v => v.name).join(", ")}
                            </div>
                          )}
                          {inc.witnesses?.length > 0 && (
                            <div className="text-[10px] text-sky-400/80 mt-0.5">
                              <span className="text-slate-500">W:</span> {inc.witnesses.map(w => w.name).join(", ")}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tab content: Call History */}
          {detailTab === "history" && (
            <div className="min-w-0 overflow-hidden">
              {loadingCallHistory ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                </div>
              ) : detailCallHistory.length === 0 ? (
                <div className="text-center py-4 text-sm text-slate-500">
                  No call history found.
                </div>
              ) : (
                <div className="space-y-1.5">
                  {detailCallHistory.map((call) => (
                    <div
                      key={call.call_id}
                      className="bg-slate-800 rounded-lg px-3 py-2 flex items-center gap-3"
                    >
                      {/* Direction icon */}
                      <div className="flex-shrink-0">
                        {call.direction?.startsWith("outbound") ? (
                          <PhoneOutgoing className="h-4 w-4 text-blue-400" />
                        ) : (
                          <PhoneIncoming className="h-4 w-4 text-green-400" />
                        )}
                      </div>
                      {/* Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-sm">
                          <span className="font-medium truncate">
                            {call.direction?.startsWith("outbound")
                              ? (call.to_number || "Unknown")
                              : (call.caller_number || "Unknown")}
                          </span>
                          {handlePhoneClick && (call.direction?.startsWith("outbound") ? call.to_number : call.caller_number) && (
                            <button
                              className="p-0.5 rounded hover:bg-slate-700 text-green-400 hover:text-green-300 shrink-0"
                              title="Call this number"
                              onClick={(e) => { e.stopPropagation(); handlePhoneClick(call.direction?.startsWith("outbound") ? call.to_number : call.caller_number); }}
                            >
                              <PhoneOutgoing className="h-3 w-3" />
                            </button>
                          )}
                          <Badge variant="outline" className={`text-[9px] px-1 py-0 ${
                            call.status === "completed" ? "border-green-600 text-green-400" :
                            call.status === "no-answer" ? "border-yellow-600 text-yellow-400" :
                            call.status === "busy" ? (!call.direction?.startsWith("outbound") ? "border-blue-600 text-blue-400" : "border-orange-600 text-orange-400") :
                            call.status === "failed" || call.status === "canceled" ? "border-red-600 text-red-400" :
                            ""
                          }`}>
                            {!call.direction?.startsWith("outbound") && call.status === "busy" ? "incoming" : call.status}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-400 mt-0.5 flex-wrap">
                          <span>{formatDateTime(call.received_at)}</span>
                          {parseInt(call.duration) > 0 && (
                            <span>&bull; {formatDuration(call.duration)}</span>
                          )}
                          <span className="text-slate-500">
                            {call.direction?.startsWith("outbound") ? "Outgoing" : "Incoming"}
                          </span>
                          {call.direction?.startsWith("outbound") && call.user_name && (
                            <span>&bull; <span className="text-slate-300">{call.user_name}</span></span>
                          )}
                          {call.direction?.startsWith("outbound") && call.call_type && (
                            <Badge variant="outline" className={`text-[9px] px-1 py-0 ${call.call_type === "call_my_phone" ? "border-purple-600 text-purple-400" : "border-slate-600 text-slate-400"}`}>
                              {call.call_type === "call_my_phone" ? "Phone" : "Browser"}
                            </Badge>
                          )}
                          {call.direction?.startsWith("outbound") && call.caller_id === "+17182221800" && (
                            <Badge variant="outline" className="text-[9px] px-1 py-0 border-amber-500 text-amber-400 font-bold">
                              CHAVERIM
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* =========== LINK CALLBACK TO INCIDENT MODAL =========== */}
      <Dialog open={linkIncidentOpen} onOpenChange={setLinkIncidentOpen}>
        <DialogContent className="max-w-md bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-4 w-4" />
              Link Callback to Incident
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-slate-400">
              Adding <span className="font-mono text-white">{detailPhone}</span> as callback
            </div>

            {/* Caller type + visual */}
            <div className="flex gap-2 items-center">
              <div className="flex-1">
                <Label className="text-xs mb-1 block">Caller Type</Label>
                <select
                  value={linkCallerType}
                  onChange={(e) => setLinkCallerType(e.target.value)}
                  className="w-full h-8 px-2 text-sm bg-slate-800 border border-slate-700 rounded text-white"
                >
                  <option value="">None</option>
                  <option value="cv">CV</option>
                  <option value="3rd_party">3rd Party</option>
                </select>
              </div>
              <div>
                <Label className="text-xs mb-1 block">Visual</Label>
                <select
                  value={linkHasVisual ? "true" : "false"}
                  onChange={(e) => setLinkHasVisual(e.target.value === "true")}
                  className="h-8 px-2 text-sm bg-slate-800 border border-slate-700 rounded text-white"
                >
                  <option value="false">No</option>
                  <option value="true">Yes</option>
                </select>
              </div>
            </div>

            {/* Incident search */}
            <div>
              <Label className="text-xs mb-1 block">Search Incident</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-slate-500" />
                <Input
                  placeholder="Search by ID, headline, address..."
                  value={linkSearch}
                  onChange={(e) => {
                    setLinkSearch(e.target.value);
                    searchIncidentsForLink(e.target.value);
                  }}
                  className="pl-8 h-8 text-sm bg-slate-800 border-slate-700"
                />
              </div>
            </div>

            {/* Results */}
            <div className="max-h-60 overflow-y-auto space-y-1">
              {linkSearching && (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                </div>
              )}
              {!linkSearching && linkSearch.length >= 2 && linkResults.length === 0 && (
                <p className="text-xs text-slate-500 text-center py-4">No incidents found</p>
              )}
              {linkResults.map((inc) => (
                <div
                  key={inc.incident_id}
                  className="p-2 bg-slate-800 rounded hover:bg-slate-700 cursor-pointer transition-colors"
                  onClick={() => handleLinkCallback(inc.incident_id)}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium truncate">{inc.headline || inc.incident_type || "Incident"}</span>
                        <Badge variant="outline" className="text-[9px] px-1 py-0">{inc.status?.replace("_", " ") || "open"}</Badge>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                        <span className="font-mono">{inc.incident_id}</span>
                        {inc.date_time && <span>&bull; {new Date(inc.date_time).toLocaleDateString("en-US", { timeZone: "America/New_York" })}</span>}
                      </div>
                      {inc.address && <div className="text-xs text-slate-500 truncate mt-0.5">{inc.address}</div>}
                    </div>
                    {linkingTo === inc.incident_id ? (
                      <Loader2 className="h-4 w-4 animate-spin text-blue-400 shrink-0" />
                    ) : (
                      <Plus className="h-4 w-4 text-blue-400 shrink-0" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* =========== ADD CALLBACK TO ACTIVE LIVE CALL MODAL =========== */}
      <Dialog open={linkLiveCallOpen} onOpenChange={setLinkLiveCallOpen}>
        <DialogContent className="max-w-md bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone className="h-4 w-4" />
              Add Callback to Live Call
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-slate-400">
              Adding <span className="font-mono text-white">{detailPhone}</span> as callback
            </div>

            {/* Caller type + visual */}
            <div className="flex gap-2 items-center">
              <div className="flex-1">
                <Label className="text-xs mb-1 block">Caller Type</Label>
                <select
                  value={linkCallerType}
                  onChange={(e) => setLinkCallerType(e.target.value)}
                  className="w-full h-8 px-2 text-sm bg-slate-800 border border-slate-700 rounded text-white"
                >
                  <option value="">None</option>
                  <option value="cv">CV</option>
                  <option value="3rd_party">3rd Party</option>
                </select>
              </div>
              <div>
                <Label className="text-xs mb-1 block">Visual</Label>
                <select
                  value={linkHasVisual ? "true" : "false"}
                  onChange={(e) => setLinkHasVisual(e.target.value === "true")}
                  className="h-8 px-2 text-sm bg-slate-800 border border-slate-700 rounded text-white"
                >
                  <option value="false">No</option>
                  <option value="true">Yes</option>
                </select>
              </div>
            </div>

            {/* Filter */}
            <div>
              <Label className="text-xs mb-1 block">Filter Active Calls</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-slate-500" />
                <Input
                  placeholder="Filter by ID, type, location..."
                  value={liveCallSearch}
                  onChange={(e) => setLiveCallSearch(e.target.value)}
                  className="pl-8 h-8 text-sm bg-slate-800 border-slate-700"
                />
              </div>
            </div>

            {/* Results */}
            <div className="max-h-60 overflow-y-auto space-y-1">
              {liveCallLoading && (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                </div>
              )}
              {!liveCallLoading && liveCallList.length === 0 && (
                <p className="text-xs text-slate-500 text-center py-4">No active live calls</p>
              )}
              {!liveCallLoading && (() => {
                const q = liveCallSearch.trim().toLowerCase();
                const filtered = q
                  ? liveCallList.filter(c =>
                      (c.call_id || "").toLowerCase().includes(q) ||
                      (c.call_type || "").toLowerCase().includes(q) ||
                      (c.location || "").toLowerCase().includes(q) ||
                      (c.cross_streets || "").toLowerCase().includes(q)
                    )
                  : liveCallList;
                if (filtered.length === 0) {
                  return <p className="text-xs text-slate-500 text-center py-4">No matches</p>;
                }
                return filtered.map((call) => (
                  <div
                    key={call.call_id}
                    className="p-2 bg-slate-800 rounded hover:bg-slate-700 cursor-pointer transition-colors"
                    onClick={() => handleLinkLiveCallCallback(call.call_id)}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium truncate">{call.call_type || "Call"}</span>
                          {call.priority === "priority" && (
                            <Badge className="bg-red-600 text-white text-[9px] px-1 py-0">PRIORITY</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                          <span className="font-mono">{call.call_id}</span>
                          {call.created_at && <span>&bull; {new Date(call.created_at).toLocaleTimeString("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit" })}</span>}
                        </div>
                        {call.location && <div className="text-xs text-slate-500 truncate mt-0.5">📍 {call.location}</div>}
                      </div>
                      {linkingToLiveCall === call.call_id ? (
                        <Loader2 className="h-4 w-4 animate-spin text-blue-400 shrink-0" />
                      ) : (
                        <Plus className="h-4 w-4 text-blue-400 shrink-0" />
                      )}
                    </div>
                  </div>
                ));
              })()}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* =========== FLAG/EDIT/NEW CONTACT MODAL =========== */}
      <Dialog open={flagOpen} onOpenChange={setFlagOpen}>
        <DialogContent className="max-w-sm bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle>{flagIsEdit ? "Edit Contact" : flagPhoneEditable ? "Add Contact" : "Flag Number"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Phone Number</Label>
              <Input
                value={flagData.phone_number}
                onChange={(e) => setFlagData({ ...flagData, phone_number: e.target.value })}
                disabled={!flagPhoneEditable}
                placeholder="+1234567890"
                className="font-mono bg-slate-800 border-slate-700"
              />
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <select
                value={flagData.status}
                onChange={(e) => setFlagData({ ...flagData, status: e.target.value })}
                className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-sm text-white"
              >
                <option value="flagged">Flagged</option>
                <option value="known">Known</option>
                <option value="suspicious">Suspicious</option>
                <option value="blocked">Blocked</option>
                <option value="vip">VIP</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Name (optional)</Label>
              <Input
                placeholder="Contact name..."
                value={flagData.name}
                onChange={(e) => setFlagData({ ...flagData, name: e.target.value })}
                className="bg-slate-800 border-slate-700"
              />
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <textarea
                placeholder="Reason for flag, notes..."
                value={flagData.notes}
                onChange={(e) => setFlagData({ ...flagData, notes: e.target.value })}
                className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-sm text-white min-h-[70px] resize-y"
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setFlagOpen(false)} size="sm">Cancel</Button>
              <Button onClick={saveFlag} size="sm">
                <Flag className="h-3.5 w-3.5 mr-1" /> Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
